import React, { useEffect, useState, useCallback } from 'react';
import NumberInput from '../../components/NumberInput';
import { FaWhatsapp, FaTelegramPlane, FaComment } from 'react-icons/fa';
import { CircularProgress, IconButton, Button } from '@mui/material';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { createPortal } from 'react-dom';
import DeliveryAlternativesList from './DeliveryAlternativesList';
import buildGazCommercialProposalBlocks from './buildGazCommercialProposalBlocks';
import {
  resolveUpsellQuantityChange,
  roundUpsellQuantityToPallet,
  palletOnlyMessage,
  isPalletOnlyUpsell,
  upsellPalletsOf,
  upsellQuantityForPallets,
  unitsPerPalletOfUpsell,
} from './gazUpsellQuantityRules';
import { resolveGazCashTotal } from './gazCashRounding';
import { buildUpsellLoadingNorm, getOfferFreePalletsForUpsells } from './upsellLoadingNorms';
import { filterUpsellItems } from './upsellSearch';
import buildGazVehicleLoadModel, { buildGazTripsTooltipText } from './buildGazVehicleLoadModel';
import GazVehicleLoadVisual, { buildFreeSpaceText } from './GazVehicleLoadVisual';
import GazProposalSummary, { buildGazProposalTotals } from './GazProposalSummary';
// Формат итога берётся из модуля, а не из компонента: он один на оба пути — и на
// нетронутый расчёт, и на ручные цены — и проверяется исполнением.
import { formatGazPayable } from './gazProposalTotals.js';
import GazUpsellModal from './GazUpsellModal';
import { palletPlacesWord, palletsWord } from '../../utils/gazDeclensions';
import { formatNomenclatureName, formatNumber, formatUnit } from '../../utils/gazTextFormat';
import { buildGazProposalHeadline } from './gazProposalHeadline';
import { buildGazOrderFacts } from './gazOrderFacts';
import { buildGazSummaryMetrics, buildGazFullTruckBasis } from './gazSummaryMetrics';
import { buildGazProposalUpsells } from './gazProposalUpsells';
import { GAZ_PRICE_ON_REQUEST, isGluePriceOnRequest } from './gazGlueAvailability';
import { buildGazSendOptions, GAZ_SEND_MODE_FULL } from './gazProposalSendOptions';
import { buildGazClientProposalModel } from './gazClientProposalText';
import GazClientProposal from './GazClientProposal';
import GazPhotoViewer from './GazPhotoViewer';
import './gazProposal.css';
import '../../theme/redesign/modal.css';
import { scrollAppIntoView } from '../../utils/pageScroll';

const formatQuantity = (quantity) => {
  const num = parseFloat(quantity);
  if (isNaN(num)) {
    return quantity;
  }

  const rounded = Math.round(num * 1000) / 1000;
  const hasDecimals = rounded % 1 !== 0;
  return hasDecimals ? rounded.toFixed(3).replace(/0+$/, '').replace(/\.$/, '') : rounded.toString();
};

const safeCeil = (value, fallback = 0) => {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return Math.ceil(num);
  }
  const fallbackNum = Number(fallback);
  return Number.isFinite(fallbackNum) ? Math.ceil(fallbackNum) : 0;
};

// Проблема с округлением цены до целого рубля перед умножением на количество актуальна
// только для кирпича (малая цена за штуку, партии в тысячи штук) — не для газобетона,
// бордюра, брусчатки и т.п., которые живут в том же блоке "Газобетон и кирпич". Поэтому
// узкая проверка именно по подкатегории material.category, а не по всей группе доставки.
const isBrickMaterial = (material) => String(material?.category || '').toLowerCase().includes('кирпич');

// Для кирпича цена за единицу мала, а берут его партиями в тысячи штук —
// округление цены до целого рубля перед умножением на количество даёт заметную переплату,
// поэтому для этой категории округляем цену до сотых, а не до целого.
const safeRoundToHundredths = (value, fallback = 0) => {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return Math.round(num * 100) / 100;
  }
  const fallbackNum = Number(fallback);
  return Number.isFinite(fallbackNum) ? Math.round(fallbackNum * 100) / 100 : 0;
};

const formatMoney = (value) => `${Math.ceil(Number(value) || 0).toLocaleString('ru-RU')} руб.`;

const formatMoneyPrecise = (value, precision = 0) => {
  const factor = 10 ** precision;
  const rounded = Math.round((Number(value) || 0) * factor) / factor;
  return `${rounded.toLocaleString('ru-RU', { minimumFractionDigits: precision, maximumFractionDigits: precision })} руб.`;
};

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  'бордюр',
  'бордюр дорожный',
  'брусчатка',
  'брусчатка и тротуарная плитка',
  'плитка тротуарная',
  'газонная решетка',
  'лоток водоотводный жби',
  'лоток водоотводный тротуарный',
]);

const isGazDeliveryCategory = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized.includes('газобетон') || GAZ_DELIVERY_CATEGORY_NAMES.has(normalized);
};

const isGazobetonOffer = (materials = []) => Array.isArray(materials) && materials.some((material) => {
  const calculator = String(material?.calculator || material?.calculatorCategory || '').trim().toLowerCase();
  const category = String(material?.category || '').trim().toLowerCase();
  const productGroup = String(material?.product_group || material?.productGroup || '').trim().toLowerCase();
  const name = String(material?.name || '').trim().toLowerCase();

  return (
    isGazDeliveryCategory(calculator) ||
    isGazDeliveryCategory(category) ||
    isGazDeliveryCategory(productGroup) ||
    name.includes('газобетон') ||
    productGroup.startsWith('u-блок') ||
    name.startsWith('u-блок')
  );
});

const getOfferMaterialPalletCount = (material) => {
  const directPallets = Number(material?.numberPallets);
  if (Number.isFinite(directPallets) && directPallets > 0) {
    return directPallets;
  }

  const occupiedPallets = Number(material?.occupiedPallets);
  if (Number.isFinite(occupiedPallets) && occupiedPallets > 0) {
    return occupiedPallets;
  }

  const totalPallets = Number(material?.totalPallets);
  if (Number.isFinite(totalPallets) && totalPallets > 0) {
    return totalPallets;
  }

  const quantity = Number(material?.quantity);
  const palletUnits = Number(material?.palletUnits);
  if (Number.isFinite(quantity) && quantity > 0 && Number.isFinite(palletUnits) && palletUnits > 0) {
    return quantity / palletUnits;
  }

  return 0;
};

const InsulationFriendsList = ({ items, onSelectMaterial, onRemoveMaterial, currentIndex, isOriginal, colSpan, setExpandedRows, paymentMethod, offerMaterials, freePalletsForUpsells, isMskGazobetonScope = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [errors, setErrors] = useState({});
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    const initialQuantities = items.reduce((acc, item, index) => ({
      ...acc,
      [index]: '1',
    }), {});
    setQuantities(initialQuantities);
    setErrors({});
  }, [items]);

  useEffect(() => {
    // Фокусируемся на поле поиска при монтировании компонента
    if (searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100); // Небольшая задержка для корректной работы
    }
  }, []);

  const handleQuantityChange = (index, value) => {
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setQuantities((prev) => ({
        ...prev,
        [index]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [index]: parseFloat(value) === 0 || value === "" ? 'Количество не может быть 0' : undefined,
      }));
    }
  };

  const handleSelect = (item, index) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Клей без цены в область заказа не попадает: цена не подставляется ни чужая,
    // ни прошлая, поэтому позиция остаётся невыбираемой (gazGlueAvailability).
    if (isGluePriceOnRequest({ item, paymentMethod, isMskGazobetonScope })) {
      return;
    }

    const quantity = quantities[index] === '' ? 0 : parseFloat(quantities[index]) || 0;
    if (quantity === 0) {
      return;
    }
    const price = paymentMethod === 'cash'
      ? parseInt(item.price_cash) || 0
      : parseInt(item.price_non_cash) || 0;
    onSelectMaterial({
      ...item,
      quantity: formatQuantity(quantity),
      sum: price * quantity,
    });
    setExpandedRows({})
  };

  // Поиск (транслит и неверная раскладка) вынесен в upsellSearch.js: тем же фильтром
  // пользуется окно «Дополнить машину», второго поиска в проекте нет.
  const filteredItems = React.useMemo(
    () => filterUpsellItems(items, searchQuery),
    [items, searchQuery]
  );

  // Функция для проверки, добавлен ли товар в таблицу
  const isItemInOfferTable = (item) => {
    if (!offerMaterials || !Array.isArray(offerMaterials)) return false;

    return offerMaterials.some(material =>
      material.name === item.product_calculator_name
    );
  };

  const getItemOfferIndex = (item) => {
    if (!offerMaterials || !Array.isArray(offerMaterials)) return -1;

    return offerMaterials.findIndex((material) =>
      material.name === item.product_calculator_name && material.isFromInsulationFriends
    );
  };

  return (
    <tr className="rd-kp-alts-row">
      <td colSpan={colSpan} className="rd-kp-alts-cell">
        <div className="rd-kp-alts rd-kp-alts--upsell">
          <div className="rd-kp-alts__search">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Поиск по наименованию и категории"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => {
              const loadingNorm = buildUpsellLoadingNorm(item, freePalletsForUpsells);
              // Единственный признак доступности клея внутри области — цена в
              // основной таблице. Вне области признак ложный и строка прежняя.
              const priceOnRequest = isGluePriceOnRequest({ item, paymentMethod, isMskGazobetonScope });
              const offerIndex = getItemOfferIndex(item);
              const isSelected = offerIndex !== -1;
              const inOffer = isItemInOfferTable(item);
              const rowClass = 'rd-kp-alts__row' + (inOffer ? ' rd-kp-alts__row--current' : '');

              return (
                <div className={rowClass} key={index}>
                  <div className="rd-kp-alts__title">
                    {item.product_calculator_name || 'Не указано'}
                    <small>{item.manufacturer || 'Не указано'} · {item.category || 'Не указано'}</small>
                    {loadingNorm && <small className="rd-kp-alts__norm">{loadingNorm.text}</small>}
                  </div>

                  <input
                    type="number"
                    className={errors[index] ? 'is-error' : undefined}
                    value={quantities[index] !== undefined ? quantities[index] : '1'}
                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                    min="0"
                    step="any"
                  />

                  <span className="rd-kp-alts__unit">{item.unit || '—'}</span>

                  <span className="rd-kp-alts__price">
                    {priceOnRequest ? GAZ_PRICE_ON_REQUEST : (
                      <>
                        {item.price_cash ? `${item.price_cash} ₽` : '—'}
                        <small className="rd-kp-alts__sub">
                          Безнал: {item.price_non_cash ? `${parseInt(item.price_non_cash).toFixed(2)} ₽` : '—'}
                        </small>
                      </>
                    )}
                  </span>

                  <button
                    type="button"
                    className={'rd-kp-alts__choose' + (isSelected ? ' rd-kp-alts__choose--remove' : '')}
                    onClick={() => (isSelected ? onRemoveMaterial(offerIndex) : handleSelect(item, index))}
                    disabled={(!isSelected && !!errors[index]) || (!isSelected && priceOnRequest)}
                    title={!isSelected && priceOnRequest ? 'Цены на этот клей нет — добавить нельзя' : undefined}
                  >
                    {isSelected ? 'Удалить' : 'Выбрать'}
                  </button>
                </div>
              );
            })
          ) : (
            <div className="rd-kp-alts__empty">Нет материалов, соответствующих запросу</div>
          )}
        </div>
      </td>
    </tr>
  );
};


// Сборка позиции заказа из строки каталога допродаж. Вынесена из
// handleSelectMaterial без изменений: тем же кодом позиция собирается и при
// выборе из обычного списка, и при пакетном добавлении из окна «Дополнить машину».
const buildMaterialFromUpsellItem = (item, paymentMethod) => {
    const selectedQuantity = parseFloat(item.quantity) || 1;
    const normalizedUnit = String(item.unit || '').trim().toLowerCase();
    const palletUnits = parseFloat(
      String(item.palletUnits ?? item.pallet_units ?? '').replace(',', '.')
    );
    const unitsPerPallet = parseFloat(
      String(item.unitsPerPallet ?? item.units_per_pallet ?? item.UNITS_PER_PALLET ?? '').replace(',', '.')
    );
    const totalPallets = parseFloat(
      String(item.totalPallets ?? item.shalanda_pallets ?? '').replace(',', '.')
    );
    const shouldUseUnitsPerPallet = normalizedUnit !== 'м3'
      && Number.isFinite(unitsPerPallet)
      && unitsPerPallet > 0;
    const calculatedNumberPallets = shouldUseUnitsPerPallet
      ? Math.ceil(selectedQuantity / unitsPerPallet)
      : Number.isFinite(palletUnits) && palletUnits > 0
        ? Math.ceil(selectedQuantity / palletUnits)
        : null;

    // Формируем объект материала для добавления в selectedOffer.materials
    const newMaterial = {
      bitrixId: item.bitrixId || item.bitrix_id || null,
      date: item.price_update_date || new Date().toLocaleDateString('ru-RU'),
      description: item.description || "Нет данных",
      name: item.product_calculator_name || item.name,
      pricePerOne: parseInt(paymentMethod === 'cash' ? item.price_cash : item.price_non_cash) || 0,
      quantity: item.quantity || 1, // Используем количество из переданного объекта
      source: item.source || item.source_table || "Не указано",
      sum: item.sum || (parseInt(paymentMethod === 'cash' ? item.price_cash : item.price_non_cash) || 0) * (parseFloat(item.quantity) || 1), // Используем переданную сумму или рассчитываем
      unit: item.unit || "упак",
      manufacturer: item.manufacturer,
      massPerOne: parseFloat(item.massperone) || 0,
      volumePerOne: parseFloat(item.volumeperone) || 0,
      category: item.category,
      calculator: item.calculator || item.product_group || 'Общестрой',
      numberPallets: calculatedNumberPallets ?? item.numberPallets ?? "-",
      palletUnits: shouldUseUnitsPerPallet
        ? unitsPerPallet
        : Number.isFinite(palletUnits) && palletUnits > 0
          ? palletUnits
          : (item.palletUnits ?? "-"),
      // Норму «штук на поддоне» кладём в саму позицию. Она уже посчитана выше, но
      // раньше оставалась только в palletUnits, где для штучного товара лежит то же
      // число под другим именем. Из-за этого правка количества в карточке машины не
      // могла проверить вместимость и упиралась в «у позиции нет нормы поддона».
      ...(shouldUseUnitsPerPallet ? { unitsPerPallet } : {}),
      totalPallets: Number.isFinite(totalPallets) && totalPallets > 0 ? totalPallets : (item.totalPallets ?? "-"),
      occupiedPallets: calculatedNumberPallets ?? item.occupiedPallets ?? "-",
      production: item.manufacturer, // Добавляем поле production для корректной фильтрации
      // Метка наличия приходит только из окна дозагрузки МСК Газобетона и только
      // когда правило сработало; в остальных случаях поля просто нет.
      ...(item?.gazStockNote ? { gazStockNote: item.gazStockNote } : {}),
      isFromInsulationFriends: true // Помечаем что материал добавлен через InsulationFriendsList
    };

    return newMaterial;
};

// Остальной код OfferTable остается без изменений
// Та же пустая ссылка, что и в Offer: пропс не должен меняться каждый рендер.
const EMPTY_PRICE_OVERRIDES = {};

const OfferTable = ({
  offer,
  offerType,
  activeOffers,
  selectedOffer,
  localPrices,
  localDeliveryPrice,
  localSkidka,
  localSkidkaD,
  localGlobalSkidka,
  per,
  tableContainerRef,
  onMaterialPriceChange,
  onSkidkaChange,
  onDeliveryPriceChange,
  onDeliveryUpdate,
  onDeliveryMethodChange,
  onSkidkaChangeDelivery,
  onGlobalSkidkaChange,
  onSkidkaOrMarzha,
  onFillBitrix,
  onSendToWazzup,
  onSendToTelegram,
  onSendToMax,
  onCreateMessage,
  commercialProposalSendMode = 'partial',
  onCommercialProposalSendModeChange,
  // Выбранный вариант заказа МСК Газобетона. Приходит сверху, потому что при смене
  // способа разгрузки весь блок КП снимается с монтирования: своё состояние он бы
  // потерял и вернулся к варианту по умолчанию. Вне области пропсы не приходят и
  // работает прежний путь.
  gazOrderVariantMode = null,
  onGazOrderVariantModeChange,
  setTooltip,
  handleMouseEnter,
  handleMouseLeave,
  options,
  data,
  title,
  selectedProduction,
  // Строки раздела «Товары» и подсказка о более дешёвом производстве — только для
  // показа внутри области. Ни расчётов, ни выбора производства они не меняют.
  orderProducts,
  gazCheapestHint,
  gazPriceOverrides = EMPTY_PRICE_OVERRIDES,
  onGazPriceOverridesChange,
  gazBrandHint,
  onSelectGazProduction,
  deliveryMethods, // Новый пропс
  onAddMaterial,
  onAddMaterials,
  onAdditionalMaterialQuantityChange,
  onRemoveMaterial,
  onBuildUnderfilledShalandaReduction,
  onApplyUnderfilledShalandaReduction,
  excludedCategories,
  paymentMethod,
  // Признак разрешённой области приходит готовым из App: здесь его не пересчитывают.
  isMskGazobetonScope = false,
  addressAndCoordinates
}) => {
  const isOriginal = offerType === 'original';
  const [source, setSource] = useState(null);
  const [materialData, setMaterialData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  // Распределение по машинам — справочная часть КП МСК Газобетона, свёрнута по умолчанию.
  // Машина, которую дополняем. null — окно закрыто.
  const [upsellVehicle, setUpsellVehicle] = useState(null);
  // Сообщение «не помещается» для конкретной строки допродажи. Ключ — индекс позиции
  // в offer.materials. Только для МСК Газобетона.
  const [upsellQuantityErrors, setUpsellQuantityErrors] = useState({});
  // Сообщение живёт только у текущего состава заказа: смена производства, региона,
  // удаление позиции или выход из области его снимают, чтобы оно не «прилипло»
  // к чужой строке. Ключи — индексы, а после удаления они сдвигаются.
  useEffect(() => {
    setUpsellQuantityErrors({});
  }, [isMskGazobetonScope, selectedProduction, addressAndCoordinates?.region, offer?.materials?.length]);

  const [deliveryAlternatives, setDeliveryAlternatives] = useState([]);
  const [isLoadingAlternatives, setIsLoadingAlternatives] = useState(false);
  const [expandedDeliveryRow, setExpandedDeliveryRow] = useState(false);
  const [showUnderfilledShalandaModal, setShowUnderfilledShalandaModal] = useState(false);
  const [underfilledShalandaPreview, setUnderfilledShalandaPreview] = useState(null);
  const [isLoadingUnderfilledPreview, setIsLoadingUnderfilledPreview] = useState(false);
  const [isApplyingUnderfilledReduction, setIsApplyingUnderfilledReduction] = useState(false);
  const [underfilledPreviewError, setUnderfilledPreviewError] = useState('');
  const [selectedReductionMaterialIndex, setSelectedReductionMaterialIndex] = useState(null);
  // Внутри МСК Газобетона старая подсказка «до полной загрузки» не показывается:
  // она считала свободное место сама, по всему рейсу и без учёта уже добавленных
  // допродаж, и расходилась с карточкой машины. Единственный интерфейс дополнения
  // там — карточка и окно «Дополнить машину», которые берут числа из общей модели.
  // Вне области подсказка работает ровно как в 5442898, сам upsellLoadingNorms.js
  // не менялся: при freePallets === null buildUpsellLoadingNorm возвращает null.
  const freePalletsForUpsells = isMskGazobetonScope ? null : getOfferFreePalletsForUpsells(offer);
  const isDeliveryLocked = Boolean(selectedOffer?.deliveryLocked || offer?.deliveryLocked);
  const isGazDeliveryOffer = isGazobetonOffer(offer?.materials);
  const canChangeDeliveryMethod = !isDeliveryLocked && !isGazDeliveryOffer;

  const resultSkidkaOrMarzha = (realSum, bazeSum, id) => {
    const parsedRealSum = Math.round(parseFloat(realSum) || 0);
    const parsedBazeSum = Math.round(parseFloat(bazeSum) || 0);
    if (parsedBazeSum === 0) return 0;
    let result = 0;
    if (id === 1) result = parsedBazeSum - parsedRealSum;
    else result = parsedRealSum - parsedBazeSum;
    return result;
  };

  useEffect(() => {
    console.log('OfferTable data:', data);
    console.log('selectedOffer.materials:', selectedOffer.materials);
  }, [selectedOffer.materials, data])

  // Функция для загрузки альтернативных способов доставки
  const loadDeliveryAlternatives = async () => {
    if (!canChangeDeliveryMethod || !offer?.materials?.length || !offer?.delivery || offer.delivery.name === 'Самовывоз') {
      return;
    }

    setIsLoadingAlternatives(true);
    try {
      const requestData = {
        materials: offer.materials.map(mat => ({
          name: mat.name,
          category: mat.calculatorCategory || offer.materials[0]?.calculatorCategory || 'Общестрой',
          productCategory: mat.category,
          quantity: mat.quantity,
          weight: mat.weight || mat.massPerOne,
          volume: mat.volume || mat.volumePerOne,
          manufacturer: mat.manufacturer || offer.production || selectedProduction
        })),
        addressAndCoordinates: addressAndCoordinates || {
          coordinates: { latitude: 59.9343, longitude: 30.3351 },
          address: "Санкт-Петербург"
        },
        selectedRegion: selectedOffer.region || addressAndCoordinates?.region || "СПБ",
        paymentMethod: paymentMethod || "cash"
      };

      console.log("🚚 Отправляем запрос к API deliveryAlternatives:", requestData);

      const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/deliveryAlternatives`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      console.log("🚚 Ответ от API deliveryAlternatives:", response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log("🚚 Данные от API deliveryAlternatives:", data);
        if (data.success) {
          setDeliveryAlternatives(data.data || []);
        }
      } else {
        const errorText = await response.text();
        console.error("❌ Ошибка API deliveryAlternatives:", response.status, response.statusText, errorText);
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки альтернативных доставок:', error);
    } finally {
      setIsLoadingAlternatives(false);
    }
  };

  // Функция для выбора альтернативного способа доставки  
  const handleSelectDeliveryAlternative = (alternative) => {
    if (!canChangeDeliveryMethod) {
      return;
    }

    // Создаем новый объект доставки на основе выбранной альтернативы
    const newDelivery = {
      name: alternative.name,
      numberOfTrips: alternative.numberOfTrips,
      costPerTrip: alternative.costPerTrip,
      totalCost: alternative.totalCost,
      bitrixId: alternative.bitrixId || offer.delivery?.bitrixId || "",
      distanceProdToClient: alternative.distanceProdToClient,
      usedCentralized: alternative.usedCentralized
    };

    // Используем handleDeliveryMethodChange для обновления глобального deliveryMethod
    // и запуска пересчета Productions
    if (onDeliveryMethodChange) {
      onDeliveryMethodChange(alternative.name, offerType);
    } else if (onDeliveryUpdate) {
      // Fallback для обычного обновления доставки
      onDeliveryUpdate(newDelivery, offerType);
    }

    // Закрываем список альтернатив
    setExpandedDeliveryRow(false);

    // Очищаем список альтернатив чтобы при следующем открытии загрузились новые
    setDeliveryAlternatives([]);
  };

  // Функция для переключения отображения альтернативных доставок
  const toggleDeliveryAlternatives = () => {
    if (!canChangeDeliveryMethod) {
      return;
    }
    if (!expandedDeliveryRow) {
      if (!deliveryMethods?.length) {
        loadDeliveryAlternatives();
      }
    }
    setExpandedDeliveryRow(!expandedDeliveryRow);
  };


  useEffect(() => {
    // Для "Оптимального маршрута" не фильтруем здесь, будем фильтровать для каждого материала отдельно
    if (selectedProduction === "Оптимальный маршрут") {
      setMaterialData([]); // Очищаем общий список, так как будем использовать индивидуальные
      return;
    }

    // Проверяем что data существует и является массивом
    if (!data || !Array.isArray(data)) {
      setMaterialData([]);
      return;
    }

    let a = data.filter((item) => item.manufacturer === selectedProduction
      && [
        "Утеплитель Роквул", "Изоляционные пленки", "Белтермо", "Утеплитель Технониколь",
        "Утеплитель Изорок", "Утеплитель Пеноплэкс", "Утеплитель Изовер", "OSB",
        "ДВП", "Фанера", "ЦСП", "Утеплитель Кнауф", "ДСП", "Комплектующие к теплоизоляции",
        "Утеплитель Урса", "Утеплитель Парок", "Гипсокартон", "Утеплитель Урса Geo",
        "Утеплитель Урса Pureone", "Утеплитель Урса Terra", "Утеплитель Урса XPS",
        "Утеплитель Технониколь 34 PN", "Утеплитель Технониколь 40 RN",
        "Утеплитель Технониколь CARBON", "Утеплитель Технониколь GreenGuard",
        "Утеплитель Технониколь Izovol", "Утеплитель Технониколь LOGICPIR",
        "Утеплитель Технониколь Master", "Утеплитель Технониколь Базалит",
        "Утеплитель Технониколь Плиты PIR", "Утеплитель Технониколь Роклайт",
        "Утеплитель Технониколь Сэндвич Ц-XPS", "Утеплитель Технониколь Теплоролл",
        "Утеплитель Технониколь Техно", "Утеплитель Технониколь Техноакустик",
        "Утеплитель Технониколь Техноблок", "Утеплитель Технониколь Техновент",
        "Утеплитель Технониколь Технолайт", "Утеплитель Технониколь Техноплекс",
        "Утеплитель Технониколь Техноруф", "Утеплитель Технониколь Техносэндвич",
        "Утеплитель Технониколь Технофас", "Утеплитель Технониколь Технофлор",
        "Утеплитель Технониколь Штукатурный фасад", "Утеплитель Роквул CONLIT",
        "Утеплитель Роквул FIRE BATTS", "Утеплитель Роквул FT Barrier",
        "Утеплитель Роквул INDUSTRIAL BATTS", "Утеплитель Роквул Klimafix",
        "Утеплитель Роквул LAMELLA MAT", "Утеплитель Роквул SeaRox",
        "Утеплитель Роквул WIRED MAT", "Утеплитель Роквул Акустик Баттс",
        "Утеплитель Роквул Арктик", "Утеплитель Роквул Бетон Элемент Баттс",
        "Утеплитель Роквул Венти Баттс", "Утеплитель Роквул Кавити Баттс",
        "Утеплитель Роквул Камин Баттс", "Утеплитель Роквул Каркас Баттс",
        "Утеплитель Роквул Лайт Баттс", "Утеплитель Роквул Пластер Баттс",
        "Утеплитель Роквул Рокфасад", "Утеплитель Роквул Руф Баттс",
        "Утеплитель Роквул Сауна Баттс", "Утеплитель Роквул Стандарт",
        "Утеплитель Роквул Сэндвич Баттс", "Утеплитель Роквул Тех Баттс",
        "Утеплитель Роквул ТЕХ МАТ", "Утеплитель Роквул Фасад Баттс",
        "Утеплитель Роквул Флор Баттс", "Утеплитель Роквул Эконом",
        "Цилиндры навивные Роквул", "Утеплитель Кнауф Climowool",
        "Утеплитель Кнауф АкустиКНАУФ", "Утеплитель Кнауф Инсулейшн",
        "Утеплитель Кнауф ТеплоКНАУФ", "Утеплитель Кнауф Терм",
        "Утеплитель Кнауф Тисма", "Утеплитель Изорок Изовент",
        "Утеплитель Изорок Изокор", "Утеплитель Изорок Изолайт",
        "Утеплитель Изорок Изоруф", "Утеплитель Изорок Изофас",
        "Утеплитель Изорок Изофлор", "Утеплитель Изорок П",
        "Утеплитель Изорок Супер", "Утеплитель Изорок Ультралайт",
        "Утеплитель Изовер KIM-AL", "Утеплитель Изовер KV",
        "Утеплитель Изовер OL", "Утеплитель Изовер Акустик",
        "Утеплитель Изовер Венти", "Утеплитель Изовер ВентФасад",
        "Утеплитель Изовер Звукозащита", "Утеплитель Изовер Изотек",
        "Утеплитель Изовер Каркас", "Утеплитель Изовер Каркасный дом",
        "Утеплитель Изовер КТ Специал", "Утеплитель Изовер Лайт",
        "Утеплитель Изовер Мастер", "Утеплитель Изовер Оптимал",
        "Утеплитель Изовер Плавающий пол", "Утеплитель Изовер Профи",
        "Утеплитель Изовер Руф", "Утеплитель Изовер Сауна",
        "Утеплитель Изовер Скатная Кровля", "Утеплитель Изовер Специал-Плита",
        "Утеплитель Изовер Стандарт", "Утеплитель Изовер Стена",
        "Утеплитель Изовер Теплая Крыша", "Утеплитель Изовер Тепло и тихо",
        "Утеплитель Изовер Теплые Стены", "Утеплитель Изовер Теплый Дом",
        "Утеплитель Изовер Фасад", "Утеплитель Изовер Флор",
        "Утеплитель Изовер Штукатурный фасад", "Утеплитель Изовер Шумка",
        "Утеплитель Хотрок Лайт-35", "Утеплитель Хотрок П",
        "Утеплитель Хотрок Руф", "Утеплитель Белтеп Лайт",
        "Утеплитель Юматекс Термо", "Утеплитель Woolex",
        "Утеплитель Тизол", "Утеплитель Изба",
        "Утеплитель Неман+", "Утеплитель Басвоул",
        "Утеплитель Дорхан", "Утеплитель Изобел",
        "Утеплитель Укна", "Утеплитель Теплекс",
        "Пенополистирол", "Утеплитель Изомин ThermoWool",
        "Утеплитель Хотрок Смарт", "Утеплитель Белтеп Флор",
        "Утеплитель Изолайф (IZOLIFE) Light", "Утеплитель Изолайф (IZOLIFE) Light+ (компрессия)",
        "Утеплитель Изолайф (IZOLIFE) Акустик", "Утеплитель Изолайф (IZOLIFE) Вент",
        "Утеплитель Изолайф (IZOLIFE) Кровля", "Утеплитель Изолайф (IZOLIFE) Л 35",
        "Утеплитель Изолайф (IZOLIFE) Л 35+ (компрессия)", "Утеплитель Изолайф (IZOLIFE) Плавающий пол",
        "Утеплитель Изолайф (IZOLIFE) СТ", "Утеплитель Изолайф (IZOLIFE) Фас",
        "Утеплитель Белтеп", "Утеплитель Белтеп Вент",
        "Утеплитель Белтеп Руф", "Утеплитель Белтеп Фасад",
        "Утеплитель Изовер Классик", "Утеплитель Изомин Руф",
        "Утеплитель Изомин Венти", "Утеплитель Изомин Фасад",
        "Утеплитель Изотек WIRED MAT", "Утеплитель КТИ",
        "Цилиндры навивные Технониколь", "Утеплитель Технониколь Изобокс",
        "Утеплитель Profiplex", "Утеплитель Profiplex XPS",
        "Утеплитель Greenplanet Арктический", "Утеплитель Изолайф (IZOLIFE) Light +"
      ].includes(item.category));
    setMaterialData(a);
  }, [data, selectedProduction]);

  useEffect(() => {
    // Проверяем что data существует и является массивом
    if (!data || !Array.isArray(data)) {
      setSource(null);
      return;
    }

    // Собираем все уникальные источники из материалов
    const allSources = offer.materials.map(material => {
      const foundItem = data.find(item =>
        item.product_calculator_name === material.name &&
        item.manufacturer === material.manufacturer
      );
      return foundItem && (foundItem.source ? foundItem.source : foundItem.source_table);
    }).filter(Boolean);

    const uniqueSources = [...new Set(allSources)];
    console.log('All sources:', allSources, 'Unique sources:', uniqueSources);

    // Показываем источник только если он один для всех материалов
    if (uniqueSources.length === 1) {
      setSource(uniqueSources[0]);
    } else {
      setSource(null); // Скрываем источник если их несколько
    }
  }, [activeOffers, data, offer.materials]);

  useEffect(() => {
    if (selectedProduction === "Оптимальный маршрут" && deliveryMethods?.length) {
      console.log('🔍 Установка deliveryAlternatives из deliveryMethods:', deliveryMethods);
      setDeliveryAlternatives(deliveryMethods);
      setIsLoadingAlternatives(false);
    } else if (offer?.materials?.length && offer?.delivery && offer.delivery.name !== 'Самовывоз') {
      loadDeliveryAlternatives();
    } else {
      setDeliveryAlternatives([]);
      setIsLoadingAlternatives(false);
    }
  }, [deliveryMethods, offer, selectedProduction]);

  const procentSkidkaOrMarzha = (realSum, bazeSum, id) => {
    const parsedRealSum = Math.round(parseFloat(realSum) || 0);
    const parsedBazeSum = Math.round(parseFloat(bazeSum) || 0);
    if (parsedBazeSum === 0) return 0;
    let result = 0;
    if (id === 1) result = Math.round(100 - (parsedRealSum / parsedBazeSum) * 100);
    else result = Math.round((parsedRealSum / parsedBazeSum - 1) * 100);
    return parseInt(Math.abs(result));
  };

  const handleSelectMaterial = useCallback((item) => {
    console.log('Selected material:', item);
    const newMaterial = buildMaterialFromUpsellItem(item, paymentMethod);
    console.log('Adding material:', newMaterial);
    // Добавляем новый материал через функцию из Offer
    onAddMaterial(newMaterial);
  }, [onAddMaterial, paymentMethod]);

  const handleRemoveMaterial = useCallback((index) => {
    console.log('Removing material at index:', index);
    onRemoveMaterial(index);
  }, [onRemoveMaterial]);

  const getTripsTooltipText = useCallback(() => {
    const numberOfTrips = parseFloat(offer?.delivery?.numberOfTrips);
    const hasValidTrips = Number.isFinite(numberOfTrips) && numberOfTrips > 0;
    const deliveryName = String(offer?.delivery?.name || '').toLowerCase();
    const deliveryBreakdown = offer?.delivery?.deliveryBreakdown || null;

    const breakdownTotalPallets = parseFloat(deliveryBreakdown?.totalPallets);
    const breakdownShalandaCapacity = parseFloat(deliveryBreakdown?.shalandaCapacity);
    const breakdownManipulatorCapacity = parseFloat(deliveryBreakdown?.manipulatorCapacity);
    const manipulatorGoesWithShalands = deliveryBreakdown?.manipulatorGoesWithShalands === true;

    const palletRows = (offer?.materials || []).filter((material) => {
      const occupiedPallets = parseFloat(material?.occupiedPallets);
      const numberPallets = parseFloat(material?.numberPallets);
      const totalPallets = parseFloat(material?.totalPallets);

      return Number.isFinite(occupiedPallets)
        || Number.isFinite(numberPallets)
        || Number.isFinite(totalPallets);
    });

    if (!hasValidTrips || palletRows.length === 0) {
      return null;
    }

    const occupiedTotal = Number.isFinite(breakdownTotalPallets) && breakdownTotalPallets > 0
      ? breakdownTotalPallets
      : palletRows.reduce((sum, material) => {
        const occupiedPallets = parseFloat(material?.occupiedPallets);
        if (Number.isFinite(occupiedPallets)) {
          return sum + occupiedPallets;
        }
        const numberPallets = parseFloat(material?.numberPallets);
        return Number.isFinite(numberPallets) ? sum + numberPallets : sum;
      }, 0);

    let totalCapacity = null;

    if (deliveryName.includes('шаланда') && Number.isFinite(breakdownShalandaCapacity) && breakdownShalandaCapacity > 0) {
      totalCapacity = numberOfTrips * breakdownShalandaCapacity;

      if (manipulatorGoesWithShalands && Number.isFinite(breakdownManipulatorCapacity) && breakdownManipulatorCapacity > 0) {
        totalCapacity += breakdownManipulatorCapacity;
      }
    } else if (
      deliveryName.includes('манипулятор')
      && Number.isFinite(breakdownManipulatorCapacity)
      && breakdownManipulatorCapacity > 0
    ) {
      totalCapacity = numberOfTrips * breakdownManipulatorCapacity;
    }

    if (!Number.isFinite(totalCapacity) || totalCapacity <= 0) {
      const capacityPerTrip = palletRows
        .map((material) => parseFloat(material?.totalPallets))
        .find((value) => Number.isFinite(value) && value > 0);

      totalCapacity = Number.isFinite(capacityPerTrip)
        ? numberOfTrips * capacityPerTrip
        : null;
    }

    const freePallets = Number.isFinite(totalCapacity)
      ? Math.max(totalCapacity - occupiedTotal, 0)
      : null;
    const exceededPallets = Number.isFinite(totalCapacity)
      ? Math.max(occupiedTotal - totalCapacity, 0)
      : null;

    return freePallets === null
      ? 'Кол-во свободных поддонов: -'
      : exceededPallets > 0
        ? `Превышение вместимости: ${exceededPallets.toLocaleString('ru-RU')} подд.`
        : `Кол-во свободных поддонов: ${freePallets.toLocaleString('ru-RU')}`;
  }, [offer]);

  const getUnderfilledShalandaWarning = useCallback(() => {
    if (!isOriginal) {
      return null;
    }

    const deliveryBreakdown = offer?.delivery?.deliveryBreakdown;
    const deliveryName = String(offer?.delivery?.name || '').toLowerCase();
    if (!deliveryBreakdown || !deliveryName.includes('шаланда')) {
      return null;
    }

    const shalandaCapacity = parseFloat(deliveryBreakdown?.shalandaCapacity);
    const shalandaCount = parseFloat(deliveryBreakdown?.shalandaCount);
    const fullShalandaCount = parseFloat(deliveryBreakdown?.fullShalandaCount);
    const remainderPallets = parseFloat(deliveryBreakdown?.remainderPallets);
    const manipulatorGoesWithShalands = deliveryBreakdown?.manipulatorGoesWithShalands === true;

    if (
      !Number.isFinite(shalandaCapacity) ||
      !Number.isFinite(shalandaCount) ||
      !Number.isFinite(fullShalandaCount) ||
      !Number.isFinite(remainderPallets)
    ) {
      return null;
    }

    if (manipulatorGoesWithShalands || remainderPallets <= 0 || shalandaCount <= fullShalandaCount) {
      return null;
    }

    if (remainderPallets > shalandaCapacity / 4) {
      return null;
    }

    return {
      shalandaCount,
      fullShalandaCount,
      underfilledPallets: remainderPallets,
    };
  }, [isOriginal, offer]);

  const underfilledShalandaWarning = getUnderfilledShalandaWarning();
  const underfilledPalletsToRemove = underfilledShalandaWarning?.underfilledPallets ?? null;

  const underfilledReductionCandidates = React.useMemo(() => {
    if (!underfilledShalandaWarning) {
      return [];
    }

    const palletsToRemove = Number(underfilledShalandaWarning.underfilledPallets);
    if (!Number.isFinite(palletsToRemove) || palletsToRemove <= 0) {
      return [];
    }

    return (offer?.materials || [])
      .map((material, index) => {
        const calculator = String(material?.calculatorCategory || material?.calculator || '').toLowerCase();
        const category = String(material?.category || '').toLowerCase();
        const isGazMaterialRow = isGazDeliveryCategory(calculator) || isGazDeliveryCategory(category);

        if (!isGazMaterialRow) {
          return null;
        }

        const palletUnits = parseFloat(material?.palletUnits);
        const quantity = parseFloat(material?.quantity);
        const quantityToRemove = Number.isFinite(palletUnits) ? palletsToRemove * palletUnits : NaN;
        const isAvailable = Number.isFinite(quantityToRemove) && Number.isFinite(quantity) && quantity >= quantityToRemove;

        return {
          index,
          name: material?.name || `Позиция ${index + 1}`,
          quantity,
          palletUnits,
          quantityToRemove,
          isAvailable,
          disabledReason: isAvailable ? '' : 'Недостаточно объема, чтобы убрать нужное количество поддонов.',
        };
      })
      .filter(Boolean);
  }, [offer?.materials, underfilledShalandaWarning]);

  const closeUnderfilledShalandaModal = useCallback(() => {
    setShowUnderfilledShalandaModal(false);
    setUnderfilledShalandaPreview(null);
    setUnderfilledPreviewError('');
    setIsLoadingUnderfilledPreview(false);
    setSelectedReductionMaterialIndex(null);
  }, []);

  const toggleUnderfilledShalandaModal = useCallback(async () => {
    if (showUnderfilledShalandaModal) {
      closeUnderfilledShalandaModal();
      return;
    }

    if (!underfilledShalandaWarning || typeof onBuildUnderfilledShalandaReduction !== 'function') {
      return;
    }

    setShowUnderfilledShalandaModal(true);
    setUnderfilledShalandaPreview(null);
    setUnderfilledPreviewError('');
    const defaultCandidate = [...underfilledReductionCandidates].reverse().find((candidate) => candidate.isAvailable)
      || underfilledReductionCandidates.find((candidate) => candidate.isAvailable);

    if (!defaultCandidate) {
      setUnderfilledPreviewError('Нет позиции, из которой можно убрать это количество поддонов.');
      return;
    }

    setSelectedReductionMaterialIndex(defaultCandidate.index);
  }, [
    closeUnderfilledShalandaModal,
    offerType,
    onBuildUnderfilledShalandaReduction,
    showUnderfilledShalandaModal,
    underfilledShalandaWarning,
    underfilledReductionCandidates,
  ]);

  useEffect(() => {
    if (!showUnderfilledShalandaModal || !Number.isInteger(selectedReductionMaterialIndex) || !Number.isFinite(Number(underfilledPalletsToRemove))) {
      return;
    }

    let isActive = true;

    const loadPreview = async () => {
      setUnderfilledShalandaPreview(null);
      setUnderfilledPreviewError('');
      setIsLoadingUnderfilledPreview(true);

      try {
        const preview = await onBuildUnderfilledShalandaReduction(
          underfilledPalletsToRemove,
          offerType,
          selectedReductionMaterialIndex
        );

        if (!isActive) {
          return;
        }

        if (!preview) {
          setUnderfilledPreviewError('Не удалось подготовить перерасчет.');
          return;
        }

        setUnderfilledShalandaPreview(preview);
      } catch (error) {
        if (!isActive) {
          return;
        }
        console.error('Ошибка при подготовке перерасчета недозаполненной шаланды:', error);
        setUnderfilledPreviewError('Не удалось подготовить перерасчет.');
      } finally {
        if (isActive) {
          setIsLoadingUnderfilledPreview(false);
        }
      }
    };

    loadPreview();

    return () => {
      isActive = false;
    };
  }, [
    offerType,
    onBuildUnderfilledShalandaReduction,
    selectedReductionMaterialIndex,
    showUnderfilledShalandaModal,
    underfilledPalletsToRemove,
  ]);

  const confirmUnderfilledShalandaReduction = useCallback(async () => {
    if (
      !underfilledShalandaWarning ||
      typeof onApplyUnderfilledShalandaReduction !== 'function' ||
      !Number.isInteger(selectedReductionMaterialIndex)
    ) {
      return;
    }

    setIsApplyingUnderfilledReduction(true);
    try {
      const applied = await onApplyUnderfilledShalandaReduction(
        underfilledPalletsToRemove,
        offerType,
        selectedReductionMaterialIndex
      );
      if (applied) {
        closeUnderfilledShalandaModal();
      } else {
        setUnderfilledPreviewError('Не удалось выполнить перерасчет.');
      }
    } catch (error) {
      console.error('Ошибка при подтверждении перерасчета недозаполненной шаланды:', error);
      setUnderfilledPreviewError('Не удалось выполнить перерасчет.');
    } finally {
      setIsApplyingUnderfilledReduction(false);
    }
  }, [
    closeUnderfilledShalandaModal,
    offerType,
    onApplyUnderfilledShalandaReduction,
    selectedReductionMaterialIndex,
    underfilledPalletsToRemove,
  ]);

  const handleTripsMouseEnter = useCallback((event) => {
    const tooltipText = getTripsTooltipText();
    if (!tooltipText) {
      setTooltip({ visible: false, text: "", x: 0, y: 0, targetElement: null });
      return;
    }

    const targetElement = event.currentTarget;
    const rect = targetElement.getBoundingClientRect();
    setTooltip({
      visible: true,
      text: tooltipText,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      targetElement,
    });
  }, [getTripsTooltipText, setTooltip]);

  const handleTripsMouseLeave = useCallback(() => {
    setTooltip({ visible: false, text: "", x: 0, y: 0, targetElement: null });
  }, [setTooltip]);

  // Tooltip для количества штук материала
  const handleMaterialQuantityMouseEnter = useCallback((e, row) => {
    if (!row.title) return;

    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      text: row.title,
      x: rect.left + rect.width / 2,
      y: rect.top,
      targetElement: e.currentTarget,
    });
  }, [setTooltip]);

  // Tooltip для газобетонных блоков — считает поддоны по конкретному блоку, а не по всему offer
  const getGazBlockTripsTooltipText = useCallback((blockKey, row) => {
    const breakdown = offer?.delivery?.deliveryBreakdown || {};
    const shalandaCapacity = parseFloat(breakdown.shalandaCapacity);
    const manipulatorCapacity = parseFloat(breakdown.manipulatorCapacity);

    const proposal = buildGazCommercialProposalBlocks(offer, { isMskGazobetonScope });
    const block = proposal.blocks.find((item) => item.key === blockKey);
    if (!block) {
      return null;
    }

    // МСК Газобетон: числа берём из той же модели загрузки, что и карточки машин.
    // Своего пересчёта из соотношения объёмов здесь больше нет — он давал другое
    // свободное место, чем карточка и подпись блока.
    if (isMskGazobetonScope) {
      if (blockKey !== 'secondary') {
        return null;
      }
      return buildGazTripsTooltipText(buildGazVehicleLoadModel(offer, proposal.blocks), row.key);
    }

    if (blockKey === 'secondary' && row.key === 'secondary-shalanda') {
      if (!Number.isFinite(shalandaCapacity) || shalandaCapacity <= 0) {
        return null;
      }

      const blockPallets = block.rows.reduce((sum, blockRow) => {
        if (blockRow.kind !== 'material' || !Number.isInteger(blockRow.originalIndex)) {
          return sum;
        }

        const sourceMaterial = offer?.materials?.[blockRow.originalIndex];
        const sourceQuantity = Number(sourceMaterial?.factVolume ?? sourceMaterial?.quantity);
        const sourcePallets = getOfferMaterialPalletCount(sourceMaterial);

        if (!Number.isFinite(sourceQuantity) || sourceQuantity <= 0 || !Number.isFinite(sourcePallets) || sourcePallets <= 0) {
          return sum;
        }

        return sum + ((Number(blockRow.quantity) || 0) / sourceQuantity) * sourcePallets;
      }, 0);

      const blockCapacity = (Number(row.quantity) || 0) * shalandaCapacity;
      const free = Math.max(blockCapacity - blockPallets, 0);
      return free > 0
        ? `Загрузка: ${Math.round(blockPallets * 1000) / 1000} из ${blockCapacity} подд. | Свободно: ${Math.round(free * 1000) / 1000}`
        : `Загрузка: ${Math.round(blockPallets * 1000) / 1000} из ${blockCapacity} подд.`;
    }

    if (blockKey === 'secondary' && row.key === 'secondary-manipulator-trip') {
      const remainderPallets = parseFloat(breakdown.remainderPallets);
      if (!Number.isFinite(manipulatorCapacity) || manipulatorCapacity <= 0) {
        return Number.isFinite(remainderPallets) ? `Загрузка: ${remainderPallets} подд.` : null;
      }
      const free = Math.max(manipulatorCapacity - (Number.isFinite(remainderPallets) ? remainderPallets : 0), 0);
      return free > 0
        ? `Загрузка: ${remainderPallets} из ${manipulatorCapacity} подд. | Свободно: ${free}`
        : `Загрузка: ${remainderPallets} из ${manipulatorCapacity} подд.`;
    }

    return null;
  }, [offer, isMskGazobetonScope]);

  const handleGazBlockTripsMouseEnter = useCallback((event, blockKey, row) => {
    const tooltipText = getGazBlockTripsTooltipText(blockKey, row);
    if (!tooltipText) {
      setTooltip({ visible: false, text: "", x: 0, y: 0, targetElement: null });
      return;
    }

    const targetElement = event.currentTarget;
    const rect = targetElement.getBoundingClientRect();
    setTooltip({
      visible: true,
      text: tooltipText,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      targetElement,
    });
  }, [getGazBlockTripsTooltipText, setTooltip]);

  const toggleRowExpansion = useCallback((index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  }, []);


  const getItemsForRow = useCallback((index) => {
    const currentMaterialCategory = offer.materials[index]?.category;
    const currentMaterial = offer.materials[index] || {};
    let itemsToShow;

    // Проверяем что data существует и является массивом
    if (!data || !Array.isArray(data)) {
      console.log('getItemsForRow: data is not available or not array, using empty array');
      return [];
    }

    // Нормализация строк для устойчивого матчинга производителя/категорий
    const normalizeValue = (value) =>
      String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

    const rowManufacturer =
      selectedProduction === "Оптимальный маршрут"
        ? (currentMaterial.production || currentMaterial.manufacturer || '')
        : (selectedProduction || currentMaterial.production || currentMaterial.manufacturer || '');

    const currentCalculator = normalizeValue(currentMaterial.calculator || currentMaterial.product_group);
    const currentCategory = normalizeValue(currentMaterial.category);
    const isGazobetonRow = isGazDeliveryCategory(currentCalculator) || isGazDeliveryCategory(currentCategory);

    // Допродажи для газобетона: только U-блоки и Клей того же производителя
    if (isGazobetonRow) {
      const normalizedRowManufacturer = normalizeValue(rowManufacturer);
      const normalizedCurrentName = normalizeValue(currentMaterial.name);

      const gazUpsellItems = data.filter((item) => {
        const itemManufacturer = normalizeValue(item.manufacturer);
        if (!itemManufacturer || itemManufacturer !== normalizedRowManufacturer) {
          return false;
        }

        const itemGroup = normalizeValue(item.product_group);
        const itemCategory = normalizeValue(item.category);

        const isUBlock = itemGroup === 'газобетон и кирпич' && itemCategory.startsWith('u-блок');
        const isGlue = itemGroup === 'газобетон и кирпич' && itemCategory === 'клей';
        const isFrostAdditive = itemGroup === 'газобетон и кирпич' && itemCategory === 'добавка противоморозная';

        if (!isUBlock && !isGlue && !isFrostAdditive) {
          return false;
        }

        // Не показываем текущий материал как допродажу к самому себе
        const itemName = normalizeValue(item.product_calculator_name || item.name);
        return itemName !== normalizedCurrentName;
      });

      console.log('🧩 [gaz-upsell] фильтрация допродаж', {
        rowName: currentMaterial.name,
        rowManufacturer,
        totalRowsInData: data.length,
        matchedRows: gazUpsellItems.length,
        sample: gazUpsellItems.slice(0, 5).map((item) => ({
          manufacturer: item.manufacturer,
          product_group: item.product_group,
          category: item.category,
          name: item.product_calculator_name,
        })),
      });

      return gazUpsellItems;
    }

    // Если это "Оптимальный маршрут", фильтруем по production конкретного материала
    if (selectedProduction === "Оптимальный маршрут" && offer.materials[index]?.production) {
      const materialProduction = offer.materials[index].production;
      itemsToShow = data.filter((item) => item.manufacturer === materialProduction
        && [
          "Утеплитель Роквул", "Изоляционные пленки", "Белтермо", "Утеплитель Технониколь",
          "Утеплитель Изорок", "Утеплитель Пеноплэкс", "Утеплитель Изовер", "OSB",
          "ДВП", "Фанера", "ЦСП", "Утеплитель Кнауф", "ДСП", "Комплектующие к теплоизоляции",
          "Утеплитель Урса", "Утеплитель Парок", "Гипсокартон", "Утеплитель Урса Geo",
          "Утеплитель Урса Pureone", "Утеплитель Урса Terra", "Утеплитель Урса XPS",
          "Утеплитель Технониколь 34 PN", "Утеплитель Технониколь 40 RN",
          "Утеплитель Технониколь CARBON", "Утеплитель Технониколь GreenGuard",
          "Утеплитель Технониколь Izovol", "Утеплитель Технониколь LOGICPIR",
          "Утеплитель Технониколь Master", "Утеплитель Технониколь Базалит",
          "Утеплитель Технониколь Плиты PIR", "Утеплитель Технониколь Роклайт",
          "Утеплитель Технониколь Сэндвич Ц-XPS", "Утеплитель Технониколь Теплоролл",
          "Утеплитель Технониколь Техно", "Утеплитель Технониколь Техноакустик",
          "Утеплитель Технониколь Техноблок", "Утеплитель Технониколь Техновент",
          "Утеплитель Технониколь Технолайт", "Утеплитель Технониколь Техноплекс",
          "Утеплитель Технониколь Техноруф", "Утеплитель Технониколь Техносэндвич",
          "Утеплитель Технониколь Технофас", "Утеплитель Технониколь Технофлор",
          "Утеплитель Технониколь Штукатурный фасад", "Утеплитель Роквул CONLIT",
          "Утеплитель Роквул FIRE BATTS", "Утеплитель Роквул FT Barrier",
          "Утеплитель Роквул INDUSTRIAL BATTS", "Утеплитель Роквул Klimafix",
          "Утеплитель Роквул LAMELLA MAT", "Утеплитель Роквул SeaRox",
          "Утеплитель Роквул WIRED MAT", "Утеплитель Роквул Акустик Баттс",
          "Утеплитель Роквул Арктик", "Утеплитель Роквул Бетон Элемент Баттс",
          "Утеплитель Роквул Венти Баттс", "Утеплитель Роквул Кавити Баттс",
          "Утеплитель Роквул Камин Баттс", "Утеплитель Роквул Каркас Баттс",
          "Утеплитель Роквул Лайт Баттс", "Утеплитель Роквул Пластер Баттс",
          "Утеплитель Роквул Рокфасад", "Утеплитель Роквул Руф Баттс",
          "Утеплитель Роквул Сауна Баттс", "Утеплитель Роквул Стандарт",
          "Утеплитель Роквул Сэндвич Баттс", "Утеплитель Роквул Тех Баттс",
          "Утеплитель Роквул ТЕХ МАТ", "Утеплитель Роквул Фасад Баттс",
          "Утеплитель Роквул Флор Баттс", "Утеплитель Роквул Эконом",
          "Цилиндры навивные Роквул", "Утеплитель Кнауф Climowool",
          "Утеплитель Кнауф АкустиКНАУФ", "Утеплитель Кнауф Инсулейшн",
          "Утеплитель Кнауф ТеплоКНАУФ", "Утеплитель Кнауф Терм",
          "Утеплитель Кнауф Тисма", "Утеплитель Изорок Изовент",
          "Утеплитель Изорок Изокор", "Утеплитель Изорок Изолайт",
          "Утеплитель Изорок Изоруф", "Утеплитель Изорок Изофас",
          "Утеплитель Изорок Изофлор", "Утеплитель Изорок П",
          "Утеплитель Изорок Супер", "Утеплитель Изорок Ультралайт",
          "Утеплитель Изовер KIM-AL", "Утеплитель Изовер KV",
          "Утеплитель Изовер OL", "Утеплитель Изовер Акустик",
          "Утеплитель Изовер Венти", "Утеплитель Изовер ВентФасад",
          "Утеплитель Изовер Звукозащита", "Утеплитель Изовер Изотек",
          "Утеплитель Изовер Каркас", "Утеплитель Изовер Каркасный дом",
          "Утеплитель Изовер КТ Специал", "Утеплитель Изовер Лайт",
          "Утеплитель Изовер Мастер", "Утеплитель Изовер Оптимал",
          "Утеплитель Изовер Плавающий пол", "Утеплитель Изовер Профи",
          "Утеплитель Изовер Руф", "Утеплитель Изовер Сауна",
          "Утеплитель Изовер Скатная Кровля", "Утеплитель Изовер Специал-Плита",
          "Утеплитель Изовер Стандарт", "Утеплитель Изовер Стена",
          "Утеплитель Изовер Теплая Крыша", "Утеплитель Изовер Тепло и тихо",
          "Утеплитель Изовер Теплые Стены", "Утеплитель Изовер Теплый Дом",
          "Утеплитель Изовер Фасад", "Утеплитель Изовер Флор",
          "Утеплитель Изовер Штукатурный фасад", "Утеплитель Изовер Шумка",
          "Утеплитель Хотрок Лайт-35", "Утеплитель Хотрок П",
          "Утеплитель Хотрок Руф", "Утеплитель Белтеп Лайт",
          "Утеплитель Юматекс Термо", "Утеплитель Woolex",
          "Утеплитель Тизол", "Утеплитель Изба",
          "Утеплитель Неман+", "Утеплитель Басвоул",
          "Утеплитель Дорхан", "Утеплитель Изобел",
          "Утеплитель Укна", "Утеплитель Теплекс",
          "Пенополистирол", "Утеплитель Изомин ThermoWool",
          "Утеплитель Хотрок Смарт", "Утеплитель Белтеп Флор",
          "Утеплитель Изолайф (IZOLIFE) Light", "Утеплитель Изолайф (IZOLIFE) Light+ (компрессия)",
          "Утеплитель Изолайф (IZOLIFE) Акустик", "Утеплитель Изолайф (IZOLIFE) Вент",
          "Утеплитель Изолайф (IZOLIFE) Кровля", "Утеплитель Изолайф (IZOLIFE) Л 35",
          "Утеплитель Изолайф (IZOLIFE) Л 35+ (компрессия)", "Утеплитель Изолайф (IZOLIFE) Плавающий пол",
          "Утеплитель Изолайф (IZOLIFE) СТ", "Утеплитель Изолайф (IZOLIFE) Фас",
          "Утеплитель Белтеп", "Утеплитель Белтеп Вент",
          "Утеплитель Белтеп Руф", "Утеплитель Белтеп Фасад",
          "Утеплитель Изовер Классик", "Утеплитель Изомин Руф",
          "Утеплитель Изомин Венти", "Утеплитель Изомин Фасад",
          "Утеплитель Изотек WIRED MAT", "Утеплитель КТИ",
          "Цилиндры навивные Технониколь", "Утеплитель Технониколь Изобокс",
          "Утеплитель Profiplex", "Утеплитель Profiplex XPS",
          "Утеплитель Greenplanet Арктический", "Утеплитель Изолайф (IZOLIFE) Light +"
        ].includes(item.category));
    } else {
      // Для обычного режима (не Оптимальный маршрут) используем materialData
      itemsToShow = [...materialData];
    }

    // Фильтруем itemsToShow: убираем товары той же категории, что и текущий материал
    if (currentMaterialCategory && itemsToShow) {

      const categoryKeywords = [
        "Утеплитель", "Изоляционные пленки", "Белтермо", "OSB", "ДВП",
        "Фанера", "ЦСП", "ДСП", "Комплектующие к теплоизоляции", "Гипсокартон", "Пенополистирол", "Цилиндры навивные Технониколь"];                                                                                                                                                                                                                                                                                                                        // Находим ключевое слово категории текущего материала                                                                                         │
      const currentCategoryKeyword = categoryKeywords.find(keyword =>
        currentMaterialCategory.includes(keyword)
      );
      if (currentCategoryKeyword) {
        itemsToShow = itemsToShow.filter(item => {
          // Исключаем все товары с категорией, содержащей то же ключевое слово                                                                      
          return !item.category.includes(currentCategoryKeyword);
        });
      } else {
        // Для неизвестных категорий - точное соответствие                                                                                           
        itemsToShow = itemsToShow.filter(item => {
          return item.category !== currentMaterialCategory;
        });
      }
    }

    return itemsToShow || [];
  }, [materialData, selectedProduction, offer.materials, data]);

  const totalSum = offer.materials.reduce((sum, mat) => sum + (parseFloat(mat.sum) || 0), 0) + (parseInt(offer.delivery?.totalCost) || 0);
  const baseSum = offer.materials.reduce((sum, mat) => sum + (parseFloat(mat.sum) || 0), 0) + (parseInt(offer.delivery?.totalCost) || 0);
  const gazCommercialProposal = buildGazCommercialProposalBlocks(offer, { isMskGazobetonScope });
  const shouldRenderGazCommercialProposal = gazCommercialProposal.isGazobetonOffer;
  // offer.materials[i].sum может быть устаревшим (округлённым сервером/ранее посчитанным значением) —
  // актуально только для кирпича (см. isBrickMaterial), поэтому для офферов с кирпичом берём точную
  // сумму из уже пересчитанных block.total; для чистого газобетона/бордюра/брусчатки — как раньше.
  const offerHasBrick = Array.isArray(offer.materials) && offer.materials.some(isBrickMaterial);
  const gazProposalTotal = shouldRenderGazCommercialProposal && offerHasBrick
    ? gazCommercialProposal.blocks.reduce((sum, block) => sum + (Number(block.total) || 0), 0)
    : null;

  // Выбранный вариант заказа. Читает его и верхняя сводка, и сам блок КП, поэтому
  // значение живёт у их общего родителя. Само состояние поднято ещё выше (App.js):
  // смена способа разгрузки снимает блок КП с монтирования, и локальное состояние
  // пропадало — выбор «только полные машины» сбрасывался на весь заказ. Пока сверху
  // ничего не выбрано, вариантом по умолчанию остаётся весь заказ.
  const [gazSendModeFallback, setGazSendModeFallback] = useState(GAZ_SEND_MODE_FULL);
  const gazSendMode = gazOrderVariantMode || gazSendModeFallback;

  const deliveryControlsEnabled = isOriginal && selectedProduction !== "ГРАНД ЛАЙН - ЦЕНТР ООО";

  // Новый вид КП собирается только внутри разрешённой области. Вне её ничего из
  // этого не считается и не рендерится — работает базовый код.
  const showGazVehicleSummary = shouldRenderGazCommercialProposal && isMskGazobetonScope;

  // Фото завода в шапке расчёта. Цель запроса приходит готовой из выбранного
  // производства (selectedOffer.nomenclatureImage): здесь строки сравнения уже
  // нет, а переформатированные материалы ни ID завода, ни guide_id не несут.
  //
  // Только у основного предложения: цель относится к выбранному производству, а
  // у вкладки аналога производство своё — чужое фото там было бы враньём.
  const gazPhotoTarget = showGazVehicleSummary && isOriginal
    ? selectedOffer?.nomenclatureImage || null
    : null;
  const [gazPhoto, setGazPhoto] = useState({ available: false, isOpen: false, urls: [], isLoading: false });

  const gazPhotoUrl = gazPhotoTarget
    ? `${process.env.REACT_APP_PATH_TO_BACKEND}/api/nomenclature-image`
      + `?productionId=${encodeURIComponent(gazPhotoTarget.productionId)}`
      + `&nomenclatureId=${encodeURIComponent(gazPhotoTarget.nomenclatureId ?? '')}`
    : null;

  // Наличие проверяется тем же запросом, что и в таблице сравнения: показывать
  // строку «посмотреть» там, где смотреть нечего, нельзя.
  useEffect(() => {
    if (!gazPhotoUrl) {
      setGazPhoto((prev) => (prev.available || prev.isOpen ? { available: false, isOpen: false, urls: [], isLoading: false } : prev));
      return undefined;
    }

    let isCancelled = false;

    fetch(gazPhotoUrl, { credentials: 'include' })
      .then((response) => response.json())
      .then((result) => {
        if (isCancelled) {
          return;
        }
        const urls = Array.isArray(result?.imageUrls) ? result.imageUrls.filter(Boolean) : [];
        setGazPhoto((prev) => ({ ...prev, available: urls.length > 0, urls }));
      })
      .catch(() => {
        if (!isCancelled) {
          setGazPhoto((prev) => ({ ...prev, available: false, urls: [] }));
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [gazPhotoUrl]);

  // При открытии список перечитывается: вкладка живёт долго, а фото могли
  // добавить уже после того, как расчёт посчитался.
  const handleOpenGazPhoto = useCallback(() => {
    setGazPhoto((prev) => ({ ...prev, isOpen: true, isLoading: true }));

    fetch(gazPhotoUrl, { credentials: 'include', cache: 'no-store' })
      .then((response) => response.json())
      .then((result) => {
        const urls = Array.isArray(result?.imageUrls) ? result.imageUrls.filter(Boolean) : [];
        setGazPhoto({ available: urls.length > 0, isOpen: true, urls, isLoading: false });
      })
      .catch(() => setGazPhoto((prev) => ({ ...prev, isOpen: true, isLoading: false })));
  }, [gazPhotoUrl]);

  const handleCloseGazPhoto = useCallback(() => {
    setGazPhoto((prev) => ({ ...prev, isOpen: false }));
  }, []);
  const gazVehicleLoadModel = showGazVehicleSummary
    ? buildGazVehicleLoadModel(offer, gazCommercialProposal.blocks)
    : null;
  // Варианты готового КП. Новых расчётов не делают: собираются теми же функциями,
  // что стояли за прежними кнопками сценария. Вне области список пуст.
  const gazSendOptions = showGazVehicleSummary && isOriginal
    ? buildGazSendOptions({ offer, paymentMethod, isMskGazobetonScope })
    : [];

  // Вариант выбирается карточками ниже, но верхняя сводка обязана показывать
  // именно его: иначе сверху стоит итог всего заказа, а выбрана неполная
  // доставка — и менеджер называет клиенту не ту сумму. Поэтому выбор живёт
  // здесь, у общего родителя сводки и блока КП.
  const gazActiveSendOption = gazSendOptions.find((option) => option.mode === gazSendMode)
    || gazSendOptions[0]
    || null;

  // Блоки, по которым живёт вся верхняя сводка: выбранный вариант, а когда
  // выбирать не из чего — весь заказ, как раньше.
  const gazSummaryBlocks = gazActiveSendOption?.blocks || gazCommercialProposal.blocks;

  // Ручные цены КП: менеджер договорился с клиентом и правит цену материала,
  // рейса или разгрузки прямо в сообщении. Это состояние экрана — в базу, в
  // payload сделки и в сравнение производств оно не идёт, исходные цены расчёта
  // остаются нетронутыми. Живёт здесь, потому что тот же пересчитанный итог
  // обязана показывать верхняя сводка: иначе «К оплате» разойдётся с КП.
  // Ручные цены приходят сверху: их читает не только экран, но и сборка товаров
  // сделки, которая живёт в Offer. Своего состояния здесь не заводится, иначе
  // экран и сделка снова разойдутся.
  const setGazPriceOverrides = useCallback((next) => {
    if (typeof onGazPriceOverridesChange === 'function') {
      onGazPriceOverridesChange(offerType, next || {});
    }
  }, [onGazPriceOverridesChange, offerType]);

  // Строки, суммы, итог и текст КП — одной моделью на обоих потребителей.
  const gazProposalPriceModel = showGazVehicleSummary && gazActiveSendOption
    ? buildGazClientProposalModel({
      offer: gazActiveSendOption.payload,
      paymentMethod,
      isMskGazobetonScope: true,
      priceOverrides: gazPriceOverrides,
    })
    : null;

  // Ушли из области — согласованная цена больше не действует: там другой регион,
  // другие цены и, скорее всего, другой завод. Держать её в памяти незачем, а
  // всплыть при возврате она не должна. Удобство «вернулся — цена на месте» тут
  // не стоит риска: адрес туда-обратно в пределах разговора почти не гоняют.
  //
  // Отдельно от построчного сброса ниже и намеренно эффектом: выход из области —
  // это смена пропса, а не пересчёт строк, и пустая модель КП здесь ничего не
  // значит, её вне области и не бывает.
  useEffect(() => {
    if (isMskGazobetonScope || Object.keys(gazPriceOverrides).length === 0) {
      return;
    }
    setGazPriceOverrides({});
  }, [isMskGazobetonScope, gazPriceOverrides, setGazPriceOverrides]);

  // Ручная цена живёт у своей строки расчёта. Сменились производство, состав или
  // количество товаров, вариант заказа, способ разгрузки или адрес — расчёт
  // пересобирается, и часть строк становится другой. Тот приём «состояние
  // подстраивается под новые пропсы», что уже используется в блоке КП: без
  // эффекта и лишнего рендера-эха.
  const gazPriceResetKey = [
    gazProposalPriceModel?.baseKey || '',
    gazSendMode,
    String(addressAndCoordinates?.address || ''),
    String(addressAndCoordinates?.latitude ?? ''),
    String(addressAndCoordinates?.longitude ?? ''),
  ].join('~');
  const [lastGazPriceResetKey, setLastGazPriceResetKey] = useState(gazPriceResetKey);
  if (gazPriceResetKey !== lastGazPriceResetKey) {
    setLastGazPriceResetKey(gazPriceResetKey);
    // Снимается не всё подряд, а только то, чья строка изменилась. Ключ строки
    // содержит цену расчёта, поэтому у подорожавшего рейса он другой — такая
    // ручная цена протухла и уходит. А цена газобетона при смене адреса остаётся
    // верной: менеджер согласовал её с клиентом, и терять её из-за пересчёта
    // доставки нельзя — она уйдёт и в сообщение, и в сделку.
    //
    // Пустая модель означает, что расчёта сейчас нет: сверять не с чем, поэтому
    // ничего не трогаем.
    const liveLineIds = new Set([
      ...(gazProposalPriceModel?.materialLines || []),
      ...(gazProposalPriceModel?.deliverySectionLines || []),
    ].map((line) => line.id));

    const savedIds = Object.keys(gazPriceOverrides);
    if (savedIds.length > 0 && liveLineIds.size > 0) {
      const kept = {};
      for (const id of savedIds) {
        if (liveLineIds.has(id)) {
          kept[id] = gazPriceOverrides[id];
        }
      }
      if (Object.keys(kept).length !== savedIds.length) {
        setGazPriceOverrides(kept);
      }
    }
  }

  const gazProposalTotals = showGazVehicleSummary
    ? buildGazProposalTotals(gazSummaryBlocks)
    : null;

  // Разгрузка показывается своей величиной даже тогда, когда в КП она зашита в
  // строку рейса манипулятора. Сумма трёх величин от этого не меняется.
  const gazSummaryMetrics = gazProposalTotals
    ? buildGazSummaryMetrics({
      blocks: gazSummaryBlocks,
      totals: gazProposalTotals,
      unloadingCost: offer?.delivery?.deliveryBreakdown?.unloadingCost,
    })
    : null;

  // Цена за м³ при погрузке полной шаланды считается по варианту полных машин, а не
  // по выбранному: переключение вариантов заказа этот показатель не двигает. Своего
  // расчёта заказа нет — берутся блоки уже собранного варианта.
  const gazFullTruckBasis = showGazVehicleSummary
    ? buildGazFullTruckBasis({
      options: gazSendOptions,
      unloadingCost: offer?.delivery?.deliveryBreakdown?.unloadingCost,
    })
    : null;

  // Наличный итог МСК Газобетона округляется до ближайших 500 ₽. Берётся уже
  // посчитанный итог выбранного предложения — своего расчёта нет, материалы,
  // доставка и цены за единицу не трогаются. Вне области и при безналичной
  // оплате resolveGazCashTotal возвращает сумму как есть.
  const gazSourceTotal = gazProposalTotal != null ? gazProposalTotal : Math.ceil(totalSum);
  const gazCashTotal = gazActiveSendOption
    // У варианта итог уже посчитан и округлён той же функцией — второй раз не считаем.
    ? {
      applies: gazActiveSendOption.roundingApplies,
      total: gazActiveSendOption.total,
      difference: gazActiveSendOption.roundingDifference,
    }
    : resolveGazCashTotal({
      total: gazSourceTotal,
      isMskGazobetonScope,
      paymentMethod,
    });

  // Единственный источник вариантов дополнения: тот же результат, по которому
  // решается, показывать ли кнопку, и который фильтруется поиском в окне.
  // Считается только внутри области.
  const gazUpsellItems = showGazVehicleSummary
    ? Object.values((offer.materials || []).reduce((acc, material, index) => {
      const category = String(material?.category || '').toLowerCase();
      const calculator = String(material?.calculator || material?.product_group || '').toLowerCase();
      if (!isGazDeliveryCategory(category) && !isGazDeliveryCategory(calculator)) {
        return acc;
      }
      for (const item of getItemsForRow(index)) {
        const key = `${String(item.product_calculator_name || '').trim().toLowerCase()}::${String(item.manufacturer || '').trim().toLowerCase()}`;
        if (!acc[key]) {
          acc[key] = item;
        }
      }
      return acc;
    }, {}))
    : [];

  const hasUpsellOptions = gazUpsellItems.length > 0;

  // Точная номенклатура выбранного производства для верхней сводки. Берётся из
  // позиций самого предложения: при смене производства меняется offer, значит
  // сводка обновляется вместе с ним. Вне области функция возвращает null.
  const gazProposalHeadline = buildGazProposalHeadline({
    offer,
    production: offer?.production || selectedProduction,
    isMskGazobetonScope,
  });

  // Что просил клиент и что фактически отгрузим. Считается из уже собранного
  // состава груза блоков — своего распределения здесь нет.
  const gazOrderFacts = buildGazOrderFacts({
    blocks: gazSummaryBlocks,
    products: orderProducts,
    vehiclesCount: gazActiveSendOption?.vehicleModel?.totalVehicles
      || gazVehicleLoadModel?.totalVehicles,
    isMskGazobetonScope: showGazVehicleSummary,
  });

  // Наличный итог уже округлён до 500 ₽ — об этом говорит сама подпись, а не
  // отдельная строка о размере округления.
  const gazTotalLabel = isOriginal
    ? (gazCashTotal.applies ? 'К оплате наличными' : 'К оплате')
    : 'Итого';

  // Цену правили руками — «К оплате» показывает тот же итог, что и КП. Берётся он
  // готовым из модели КП: второго округления и второй формулы здесь не заводится.
  const gazManualTotalValue = gazProposalPriceModel?.hasManualPrices
    ? formatGazPayable(gazProposalPriceModel.payable, gazProposalPriceModel.cashApplies)
    : null;

  // Выбранный вариант нужен и сводке, и остальным путям отправки (заполнение
  // сделки), поэтому запоминается здесь и тем же вызовом уходит наверх.
  const handleGazSendModeChange = useCallback((mode) => {
    setGazSendModeFallback(mode);
    // Тот же выбор запоминается выше по дереву, чтобы пережить пересчёт после смены
    // способа разгрузки. Второго варианта это не заводит: значение одно и то же.
    if (typeof onGazOrderVariantModeChange === 'function') {
      onGazOrderVariantModeChange(mode);
    }
    if (typeof onCommercialProposalSendModeChange === 'function') {
      onCommercialProposalSendModeChange(mode);
    }
  }, [onCommercialProposalSendModeChange, onGazOrderVariantModeChange]);

  // Переход к уже существующей таблице сравнения производств.
  const handleShowComparison = () => {
    const node = document.getElementById('gaz-productions-compare');
    if (node) {
      scrollAppIntoView(node);
    }
  };

  // Добавленные допродажи с их индексами в offer.materials: второго массива
  // товаров не заводится, индексы нужны существующим обработчикам правки и
  // удаления. Вне области список пуст — там допродажи показываются как раньше.
  const gazProposalUpsells = buildGazProposalUpsells({ offer, isMskGazobetonScope });

  // Свободные паллетоместа машины остатка — то же число, что показывают карточка,
  // подпись блока и окно дополнения. Своей вместимости здесь не считается.
  const gazRemainderVehicle = (gazVehicleLoadModel?.vehicles || []).find((vehicle) => vehicle.isRemainder) || null;

  /**
   * Правка количества уже добавленной допродажи внутри МСК Газобетона.
   *
   * Уменьшение разрешено всегда. Увеличение — только если ПРИРОСТ паллетомест
   * помещается в свободное место машины: дополнение занимает уже оплаченный рейс,
   * и молча выходить за него нельзя. Значение не обрезается и не округляется —
   * изменение просто не применяется, а рядом с полем появляется сообщение.
   *
   * Расчёта доставки здесь нет: сравниваются готовые паллетоместа и готовое
   * свободное место из модели.
   */
  const handleAdditionalQuantityChange = (materialIndex, nextQuantity) => {
    const decision = resolveUpsellQuantityChange({
      material: offer.materials?.[materialIndex],
      nextQuantity,
      freePallets: gazRemainderVehicle?.freePallets,
    });

    if (!decision.allowed) {
      // Значение не применяем и не обрезаем — показываем, чего не хватает.
      setUpsellQuantityErrors((prev) => ({ ...prev, [materialIndex]: decision.message }));
      return;
    }

    setUpsellQuantityErrors((prev) => {
      if (!(materialIndex in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[materialIndex];
      return next;
    });
    onAdditionalMaterialQuantityChange(materialIndex, nextQuantity);
  };

  /**
   * Количество дотягивается до целого поддона, когда менеджер закончил набирать.
   *
   * Проверять на каждой цифре нельзя: набирая «108», человек проходит через «1»,
   * и поле прыгало бы к поддону после первого же нажатия. Поэтому правило
   * срабатывает на выходе из поля.
   *
   * Дальше значение идёт обычным путём, вместе с проверкой свободного места: если
   * целый поддон не помещается, изменение не применится и рядом появится
   * объяснение.
   */
  const handleAdditionalQuantityBlur = (materialIndex) => {
    const material = offer.materials?.[materialIndex];
    const rounded = roundUpsellQuantityToPallet({ material, quantity: material?.quantity });
    if (!rounded || !rounded.changed) {
      return;
    }

    handleAdditionalQuantityChange(materialIndex, String(rounded.quantity));
    setUpsellQuantityErrors((prev) => ({
      ...prev,
      [materialIndex]: palletOnlyMessage(rounded.perPallet, rounded.quantity),
    }));
  };

  /**
   * Поле количества у U-блоков считает ПОДДОНЫ, а не штуки: завод отгружает их
   * только целыми поддонами, и менеджер заказывает поддонами. Штуки калькулятор
   * подставляет сам и показывает рядом.
   */
  const handleAdditionalPalletsChange = (materialIndex, nextPallets) => {
    const material = offer.materials?.[materialIndex];

    // Меньше поддона не бывает: убрать позицию целиком можно крестиком рядом.
    if (!(Number(nextPallets) >= 1)) {
      return;
    }

    const quantity = upsellQuantityForPallets(material, nextPallets);
    if (quantity === null) {
      return;
    }
    handleAdditionalQuantityChange(materialIndex, String(quantity));
  };

  /**
   * Количество позиции дозагрузки.
   *
   * У U-блоков — тот же счётчик, что и в окне подбора: количество показано в
   * штуках, а «−» и «+» шагают целым поддоном. Набирать число руками там нельзя,
   * потому что завод отгружает только поддонами.
   *
   * У клея и добавки поле осталось обычным: они продаются мешками и вёдрами.
   */
  const renderUpsellQuantityField = (materialIndex, style) => {
    const material = offer.materials?.[materialIndex];
    const perPallet = unitsPerPalletOfUpsell(material);

    if (isPalletOnlyUpsell(material) && perPallet) {
      const pallets = upsellPalletsOf(material) ?? 0;
      const freePallets = gazRemainderVehicle?.freePallets;
      const noRoomForMore = Number.isFinite(Number(freePallets)) && Number(freePallets) < 1;

      return (
        <span className="gaz-kp-upsell__stepper" style={style}>
          <span className="gaz-kp-upsell__stepper-row">
            <button
              type="button"
              onClick={() => handleAdditionalPalletsChange(materialIndex, pallets - 1)}
              disabled={pallets <= 1}
              title="Убрать поддон"
              aria-label="Убрать поддон"
            >
              −
            </button>
            <span className="gaz-kp-upsell__stepper-value">{formatNumber(material?.quantity)}</span>
            <button
              type="button"
              onClick={() => handleAdditionalPalletsChange(materialIndex, pallets + 1)}
              disabled={noRoomForMore}
              title={noRoomForMore ? 'Больше не помещается в машину' : 'Добавить поддон'}
              aria-label="Добавить поддон"
            >
              +
            </button>
          </span>
          <small className="gaz-kp-upsell__stepper-hint">
            {pallets} {palletsWord(pallets)} × {formatNumber(perPallet)} шт.
          </small>
        </span>
      );
    }

    return (
      <NumberInput
        value={material?.quantity}
        onChange={(value) => handleAdditionalQuantityChange(materialIndex, value)}
        onBlur={() => handleAdditionalQuantityBlur(materialIndex)}
        style={style}
        allowDecimal
        maxDecimals={3}
      />
    );
  };

  const handleUpsellSubmit = (selectedItems) => {
    const materials = (Array.isArray(selectedItems) ? selectedItems : [])
      .map((item) => buildMaterialFromUpsellItem(item, paymentMethod));
    setUpsellVehicle(null);
    if (materials.length === 0 || !onAddMaterials) {
      return;
    }
    // Только пакетным обработчиком: одиночное добавление читает состав заказа на момент
    // вызова, поэтому вызов его в цикле сохранил бы лишь последнюю позицию.
    onAddMaterials(materials);
  };

  // Внутри карточки машины остатка: сначала уже добавленные допродажи, затем кнопка
  // дополнения. Список показывается независимо от того, осталось ли место — иначе
  // добавленный товар исчезал бы из вида ровно тогда, когда машина стала полной.
  // Кнопка появляется только когда варианты реально есть и место ещё осталось:
  // нет вариантов — нет ни кнопки, ни обещаний.
  /**
   * Машины варианта — внутрь его же карточки.
   *
   * У «всего заказа» это ровно та модель и тот же слот дозагрузки, что стояли
   * отдельным блоком: состав машин, дозагрузка и суммы не меняются. У «только
   * полностью загруженных машин» — модель самого варианта; дозагрузки там нет,
   * потому что она живёт в машине остатка, а её в этом варианте нет.
   */
  const renderVariantVehicles = (option) => (option?.isPartialOrder
    ? <GazVehicleLoadVisual model={option.vehicleModel} />
    : <GazVehicleLoadVisual model={gazVehicleLoadModel} addUpsellSlot={renderUpsellSlot} />);

  const renderUpsellSlot = (vehicle) => {
    // Допродажи занимают уже оплаченное место машины остатка, поэтому и показываются
    // в ней. Если машина одна — она же и есть машина остатка.
    const isUpsellVehicle = gazRemainderVehicle
      ? vehicle === gazRemainderVehicle
      : gazVehicleLoadModel?.vehicles?.length === 1;
    const upsells = isUpsellVehicle ? gazProposalUpsells : [];

    const canAddMore = hasUpsellOptions && !vehicle.isFull && !vehicle.isOverloaded
      && !vehicle.hasUnmeasuredCargo && vehicle.freePallets > 0;

    if (upsells.length === 0 && !canAddMore) {
      return null;
    }

    // Свободное место называет тот блок, в котором стоит кнопка: числа и действие
    // рядом. Текст общий с карточкой машины — расходиться им негде.
    const freeSpaceText = buildFreeSpaceText(vehicle);

    return (
      <div className="gaz-kp-upsells">
        {upsells.map((upsell) => (
          <div className="gaz-kp-upsell" key={`gaz-upsell-${upsell.index}`}>
            <div className="gaz-kp-upsell__main">
              <div className="gaz-kp-upsell__name">{formatNomenclatureName(upsell.name)}</div>
              <div className="gaz-kp-upsell__meta">
                {formatNumber(upsell.quantity)} {formatUnit(upsell.unit)}
                {' · '}
                {upsell.palletPlaces !== null
                  ? `${upsell.palletPlaces} ${palletPlacesWord(upsell.palletPlaces)}`
                  : 'место не посчитано'}
              </div>
              {upsell.stockNote && (
                <div className="gaz-kp-upsell__stock">{upsell.stockNote}</div>
              )}
            </div>

            <div className="gaz-kp-upsell__controls">
              {isOriginal && onAdditionalMaterialQuantityChange
                && renderUpsellQuantityField(upsell.index)}
              {isOriginal && onRemoveMaterial && (
                <button
                  type="button"
                  className="gaz-kp-upsell__remove"
                  onClick={() => onRemoveMaterial(upsell.index)}
                  title="Удалить позицию"
                  aria-label={`Удалить ${upsell.name}`}
                >
                  ×
                </button>
              )}
            </div>

            <div className="gaz-kp-upsell__sum">
              {Math.ceil(upsell.sum).toLocaleString('ru-RU')} ₽
            </div>

            {upsellQuantityErrors[upsell.index] && (
              <div role="alert" className="gaz-kp-upsell__error">
                {upsellQuantityErrors[upsell.index]}
              </div>
            )}
          </div>
        ))}

        {/* Дополнение машины — необязательное действие: оно оформлено слабее, чем
            копирование КП. Кнопка называет само действие, а сколько места
            осталось, уже сказано строкой выше — в карточке своей машины. */}
        {freeSpaceText && (
          <p className="gaz-kp-addmore__hint">{freeSpaceText}</p>
        )}

        {canAddMore && (
          <div className="gaz-kp-addmore">
            <button
              type="button"
              className="gaz-kp-addmore__btn"
              onClick={() => setUpsellVehicle(vehicle)}
            >
              Дозагрузить машину
            </button>
          </div>
        )}
      </div>
    );
  };

  // Колонка скидки внутри МСК Газобетона не показывается: в новом КП её не должно
  // быть по согласованному плану. Это только интерфейс — поля скидки в состоянии
  // и расчёт цены не трогаются, вне области колонка работает как в 5442898.
  const showGazSkidkaColumn = isOriginal && !isMskGazobetonScope;
  const gazBlockColSpan = showGazSkidkaColumn ? 7 : 6;

  const renderGazBlockRow = (row, rowIndex, blockKey) => {
    const isMaterialRow = row.kind === 'material' && Number.isInteger(row.originalIndex);
    const originalIndex = isMaterialRow ? row.originalIndex : null;
    const currentOfferMaterial = isMaterialRow ? offer.materials?.[originalIndex] : null;
    const extraItems = isMaterialRow && blockKey === 'secondary' ? getItemsForRow(originalIndex) : [];
    const hasExtraItems = extraItems.length > 0;
    const isExpanded = isMaterialRow && expandedRows[originalIndex];
    const isRemovableAdditionalMaterial = Boolean(
      isOriginal
      && blockKey === 'secondary'
      && currentOfferMaterial?.isFromInsulationFriends
    );
    const hasRowControls = hasExtraItems || isRemovableAdditionalMaterial;
    const isBrickRow = isMaterialRow && isBrickMaterial(currentOfferMaterial);
    const displayPrice = isMaterialRow
      ? (isBrickRow
        ? (isOriginal ? safeRoundToHundredths(localPrices[originalIndex], row.pricePerOne) : safeRoundToHundredths(row.pricePerOne))
        : (isOriginal ? safeCeil(localPrices[originalIndex], row.pricePerOne) : safeCeil(row.pricePerOne)))
      : safeCeil(row.pricePerOne);
    const displaySum = row.kind === 'material'
      ? (isBrickRow
        ? Math.round((Number(row.quantity) || 0) * displayPrice * 100) / 100
        : Math.ceil((Number(row.quantity) || 0) * displayPrice))
      : Math.ceil(row.sum);

    return (
      <React.Fragment key={`${blockKey}-${row.key}`}>
        <tr>
          <td className="text-center">{rowIndex + 1}</td>
          <td className="rd-kp-td--text">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', position: 'relative', width: '100%' }}>
              <span
                style={{
                  textAlign: 'left',
                  paddingRight: hasRowControls ? '140px' : '0',
                  width: '100%',
                }}
              >
                {row.name}
              </span>
              {hasRowControls && (
                <div
                  style={{
                    position: 'absolute',
                    right: '0',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {isRemovableAdditionalMaterial && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleRemoveMaterial(originalIndex)}
                      sx={{
                        minWidth: 'auto',
                        fontSize: '12px',
                        padding: '4px 8px',
                        textTransform: 'none',
                        borderColor: '#d32f2f',
                        color: '#d32f2f',
                        '&:hover': {
                          borderColor: '#b71c1c',
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                        },
                      }}
                    >
                      Удалить
                    </Button>
                  )}
                  {hasExtraItems && (
                    <button
                      onClick={() => toggleRowExpansion(originalIndex)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #1976d2',
                        backgroundColor: 'transparent',
                        color: '#1976d2',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(event) => {
                        event.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {isExpanded ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                      <span>{extraItems.length}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </td>
          <td
            className="text-center"
            style={isMaterialRow && row.title ? { cursor: 'help' } : undefined}
            onMouseEnter={isMaterialRow && row.title ? (e) => handleMaterialQuantityMouseEnter(e, row) : undefined}
            onMouseLeave={isMaterialRow && row.title ? handleTripsMouseLeave : undefined}
          >
            {/* Количество добавленной допродажи можно поправить прямо в строке —
                только внутри МСК Газобетона и только у позиции из каталога.
                Во всех остальных случаях ячейка остаётся текстом, как в базе. */}
            {isMskGazobetonScope && isOriginal && isMaterialRow && onAdditionalMaterialQuantityChange
              && offer.materials?.[originalIndex]?.isFromInsulationFriends ? (
              <>
                {renderUpsellQuantityField(originalIndex, { width: '76px' })}
                {upsellQuantityErrors[originalIndex] && (
                  <div
                    role="alert"
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      lineHeight: 1.3,
                      color: '#c62828',
                      fontWeight: 600,
                      whiteSpace: 'normal',
                      maxWidth: 180,
                    }}
                  >
                    {upsellQuantityErrors[originalIndex]}
                  </div>
                )}
              </>
            ) : (
              formatQuantity(row.quantity)
            )}
          </td>
          <td
            className="text-center"
            style={!isMaterialRow && row.unit === 'рейс' && blockKey === 'secondary' ? { whiteSpace: 'nowrap', cursor: 'help' } : undefined}
            onMouseEnter={!isMaterialRow && row.unit === 'рейс' && blockKey === 'secondary' ? (e) => handleGazBlockTripsMouseEnter(e, blockKey, row) : undefined}
            onMouseLeave={!isMaterialRow && row.unit === 'рейс' && blockKey === 'secondary' ? handleTripsMouseLeave : undefined}
          >
            {row.unit}
          </td>
          <td className="text-center">
            {isMaterialRow ? (
              <NumberInput
                value={displayPrice}
                onChange={(value) => onMaterialPriceChange(originalIndex, value, offerType)}
                style={{ width: '100px' }}
                allowDecimal={isBrickRow}
                maxDecimals={2}
              />
            ) : (
              formatMoney(displayPrice)
            )}
          </td>
          <td className="text-center">{formatMoneyPrecise(displaySum, isBrickRow ? 2 : 0)}</td>
          {showGazSkidkaColumn && (
            <td className="text-center">
              {isMaterialRow ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <NumberInput
                    value={localSkidka[originalIndex] || ''}
                    onChange={(value) => onSkidkaChange(originalIndex, value, offerType)}
                    style={{ width: '80px' }}
                  />
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>%</span>
                </div>
              ) : (/разгрузк|автокран|работе манипулятора/i.test(row.name) ? (
                <span className="rd-kp-unload-note">разгрузка всего объёма</span>
              ) : null)}
            </td>
          )}
        </tr>
        {isExpanded && hasExtraItems && (
          <InsulationFriendsList
            items={extraItems}
            onSelectMaterial={handleSelectMaterial}
            onRemoveMaterial={handleRemoveMaterial}
            currentIndex={originalIndex}
            isOriginal={isOriginal}
            colSpan={gazBlockColSpan}
            setExpandedRows={setExpandedRows}
            paymentMethod={paymentMethod}
            offerMaterials={offer.materials}
            freePalletsForUpsells={freePalletsForUpsells}
            isMskGazobetonScope={isMskGazobetonScope}
          />
        )}
      </React.Fragment>
    );
  };

  const renderGazBlockSection = (block) => {
    const isSecondaryBlock = block.key === 'secondary';
    const sectionCaption = block.title || (isSecondaryBlock ? 'Остаток и отдельные услуги' : 'Основной блок');
    let secondaryUpsellMaterialIndex = null;
    let secondaryUpsellItems = [];

    if (isSecondaryBlock) {
      const extraItemsByKey = {};

      for (let index = 0; index < offer.materials.length; index += 1) {
        const currentMaterial = offer.materials[index] || {};
        const materialCategory = String(currentMaterial.category || '').toLowerCase();
        const materialCalculator = String(currentMaterial.calculator || currentMaterial.product_group || '').toLowerCase();
        const isGazobetonMaterial = isGazDeliveryCategory(materialCategory) || isGazDeliveryCategory(materialCalculator);

        if (!isGazobetonMaterial) {
          continue;
        }

        const itemsForRow = getItemsForRow(index);
        if (!itemsForRow.length) {
          continue;
        }

        if (secondaryUpsellMaterialIndex === null) {
          secondaryUpsellMaterialIndex = index;
        }

        itemsForRow.forEach((item) => {
          const itemKey = `${String(item.product_calculator_name || '').trim().toLowerCase()}::${String(item.manufacturer || '').trim().toLowerCase()}`;
          if (!extraItemsByKey[itemKey]) {
            extraItemsByKey[itemKey] = item;
          }
        });
      }

      secondaryUpsellItems = Object.values(extraItemsByKey);
    }

    const hasSecondaryUpsellSelector = isSecondaryBlock && secondaryUpsellItems.length > 0 && secondaryUpsellMaterialIndex !== null;
    const secondaryUpsellExpandedKey = hasSecondaryUpsellSelector ? `secondary-upsell-${secondaryUpsellMaterialIndex}` : null;
    const isSecondaryUpsellExpanded = secondaryUpsellExpandedKey ? Boolean(expandedRows[secondaryUpsellExpandedKey]) : false;
    const shouldShowDeliveryLabel = offer?.delivery && offer.delivery.name !== 'Самовывоз';
    const hasSecondaryMaterialRow = isSecondaryBlock && block.rows.some((row) => row.kind === 'material');
    // Внутри МСК Газобетона строки-заглушки «Допродажи» нет: пока товар не выбран,
    // машину описывает только её фактический груз, а добавляют через окно
    // «Дополнить машину» на карточке машины. Вне области строка остаётся базовой.
    const shouldRenderSecondaryUpsellRow = hasSecondaryUpsellSelector
      && !hasSecondaryMaterialRow
      && !isMskGazobetonScope;

    // Блок остатка без единой строки — это не машина, а пустая карточка: когда все
    // машины загружены полностью, остатка нет и показывать нечего. Скрываем только
    // внутри области; вне её блок рендерится со своим базовым emptyText.
    if (isMskGazobetonScope && isSecondaryBlock && block.rows.length === 0) {
      return null;
    }

    return (
      <section
        key={block.key}
        data-gaz-block-key={block.key}
        className={`rd-kp-block${isSecondaryBlock ? ' rd-kp-block--secondary' : ''}`}
      >
        <div className="rd-kp-block__head">
          <div className="rd-kp-block__caption">
            {isSecondaryBlock && <span className="rd-kp-block__badge">ВАЖНО</span>}
            {sectionCaption}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isSecondaryBlock && underfilledShalandaWarning && (
                <button
                  onClick={toggleUnderfilledShalandaModal}
                  className={`rd-kp-warn-btn${showUnderfilledShalandaModal ? ' is-active' : ''}`}
                  title="Есть недозаполненная шаланда"
                >
                  !
                </button>
              )}
              {selectedProduction && <div className="rd-kp-head__prod"><b>{selectedProduction}</b></div>}
            </div>
          </div>
          {block.subtitle && (
            <div className="rd-kp-block__subtitle">
              {block.subtitle.split(' | ').map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part.startsWith('Свободно:') ? (
                    <span className="rd-kp-warn">{part}</span>
                  ) : (
                    part
                  )}
                  {i < arr.length - 1 ? ' | ' : ''}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {block.rows.length > 0 ? (
          <>
            <table className="rd-kp-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th className="rd-kp-th--text">Наименование</th>
                  <th>Кол-во</th>
                  <th>Ед. изм.</th>
                  <th>Цена за ед.</th>
                  <th>Сумма</th>
                  {showGazSkidkaColumn && (
                    <th
                      style={{ textDecoration: 'underline', color: 'var(--rd-blue-600)', cursor: 'pointer' }}
                      onClick={onSkidkaOrMarzha}
                    >
                      {per.text}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {shouldRenderSecondaryUpsellRow && (
                  <>
                    <tr>
                      <td className="text-center">1</td>
                      <td className="rd-kp-td--text">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', position: 'relative', width: '100%' }}>
                          <span
                            style={{
                              textAlign: 'left',
                              paddingRight: '80px',
                              width: '100%',
                            }}
                          >
                            Допродажи
                          </span>
                          <div
                            style={{
                              position: 'absolute',
                              right: '0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <button
                              onClick={() => toggleRowExpansion(secondaryUpsellExpandedKey)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid #1976d2',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                fontSize: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                flexShrink: 0,
                              }}
                              onMouseEnter={(event) => {
                                event.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                              }}
                              onMouseLeave={(event) => {
                                event.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {isSecondaryUpsellExpanded ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                              <span>{secondaryUpsellItems.length}</span>
                            </button>
                          </div>
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      {isOriginal && <td></td>}
                    </tr>
                    {isSecondaryUpsellExpanded && (
                      <InsulationFriendsList
                        items={secondaryUpsellItems}
                        onSelectMaterial={handleSelectMaterial}
                        onRemoveMaterial={handleRemoveMaterial}
                        currentIndex={secondaryUpsellMaterialIndex}
                        isOriginal={isOriginal}
                        colSpan={gazBlockColSpan - 1}
                        setExpandedRows={setExpandedRows}
                        paymentMethod={paymentMethod}
                        offerMaterials={offer.materials}
                        freePalletsForUpsells={freePalletsForUpsells}
                        isMskGazobetonScope={isMskGazobetonScope}
                      />
                    )}
                  </>
                )}
                {block.rows.map((row, index) => renderGazBlockRow(row, index, block.key))}
                <tr className="rd-kp-subtotal">
                  <td colSpan={5} className="rd-kp-subtotal__label">Итого по блоку:</td>
                  <td colSpan={gazBlockColSpan - 5} className="rd-kp-subtotal__value">
                    {block.rows.some((row) => row.kind === 'material' && Number.isInteger(row.originalIndex) && isBrickMaterial(offer.materials?.[row.originalIndex]))
                      ? formatMoneyPrecise(block.total, 2)
                      : formatMoney(block.total)}
                  </td>
                  {isOriginal && <td></td>}
                </tr>
              </tbody>
            </table>
            {hasSecondaryUpsellSelector && !shouldRenderSecondaryUpsellRow && isSecondaryUpsellExpanded && (
              <table className="rd-kp-table">
                <tbody>
                  <InsulationFriendsList
                    items={secondaryUpsellItems}
                    onSelectMaterial={handleSelectMaterial}
                    onRemoveMaterial={handleRemoveMaterial}
                    currentIndex={secondaryUpsellMaterialIndex}
                    isOriginal={isOriginal}
                    colSpan={gazBlockColSpan}
                    setExpandedRows={setExpandedRows}
                    paymentMethod={paymentMethod}
                    offerMaterials={offer.materials}
                    freePalletsForUpsells={freePalletsForUpsells}
                    isMskGazobetonScope={isMskGazobetonScope}
                  />
                </tbody>
              </table>
            )}
            {!isSecondaryBlock && block.priceSummaries.length > 0 && (
              <div className="rd-kp-block__summary">
                {block.priceSummaries.map((summary) => (
                  <div
                    key={`${block.key}-${summary.materialName}`}
                    className="rd-kp-block__summary-row"
                  >
                    <span>{`${summary.materialName} (${formatQuantity(summary.volume)} ${summary.unit || 'м3'})`}</span>
                    <span className="rd-kp-block__summary-row-note">при полной погрузке шаланды</span>
                    <span>
                      {`${Math.ceil(summary.pricePerM3).toLocaleString('ru-RU')} руб. / ${summary.unit || 'м3'}${shouldShowDeliveryLabel ? ' (с учетом доставки)' : ''}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="rd-kp-block__empty">{block.emptyText}</div>
        )}
      </section>
    );
  };

  if (!isOriginal && !source) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className="rd-kp-table-wrapper" ref={tableContainerRef}>
        {!(isOriginal && shouldRenderGazCommercialProposal) && (
          <div className="rd-kp-group">
            {title || (isOriginal ? 'Исходное предложение' : 'Аналог')}
            {!isOriginal && source && <small className="rd-kp-warn">Источник: {source}</small>}
          </div>
        )}

        <div>
          {shouldRenderGazCommercialProposal ? (
            <div style={{ display: 'grid', gap: '20px' }}>
              {/* {deliveryControlsEnabled && offer.delivery && offer.delivery.name !== 'Самовывоз' && (
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div className="rd-kp-delivery-params">
                    <div className="rd-kp-delivery-params__title">Параметры доставки</div>
                    <div className="rd-kp-delivery-params__controls">
                      <div className="rd-kp-delivery-params__field">
                        <span>Цена доставки за рейс:</span>
                        <NumberInput
                          value={localDeliveryPrice}
                          onChange={(value) => onDeliveryPriceChange(value, offerType)}
                          style={{ width: '100px' }}
                        />
                      </div>
                      <div className="rd-kp-delivery-params__field">
                        <span>Скидка на доставку:</span>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <NumberInput
                            value={localSkidkaD || ''}
                            onChange={(value) => onSkidkaChangeDelivery(value, offerType)}
                            style={{ width: '80px' }}
                          />
                          <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>%</span>
                        </div>
                      </div>
                      {underfilledShalandaWarning && (
                        <button
                          onClick={toggleUnderfilledShalandaModal}
                          className={`rd-kp-warn-btn${showUnderfilledShalandaModal ? ' is-active' : ''}`}
                          title="Есть недозаполненная шаланда"
                        >
                          !
                        </button>
                      )}
                      {canChangeDeliveryMethod && (
                        <button
                          onClick={toggleDeliveryAlternatives}
                          className="rd-kp-inline-btn"
                        >
                          {expandedDeliveryRow ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                          <span>Другие</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {expandedDeliveryRow && canChangeDeliveryMethod && (
                    <table className="rd-kp-table">
                      <tbody>
                        <DeliveryAlternativesList
                          alternatives={deliveryAlternatives}
                          onSelectDelivery={handleSelectDeliveryAlternative}
                          colSpan={7}
                          setExpandedRows={setExpandedDeliveryRow}
                          currentDelivery={offer.delivery}
                          paymentMethod={paymentMethod}
                          isLoading={isLoadingAlternatives}
                        />
                      </tbody>
                    </table>
                  )}
                </div>
              )} */}

              {/* МСК Газобетон: сверху компактная сводка с точной номенклатурой, ниже
                  распределение по машинам и одна кнопка формирования КП. Вне области —
                  базовый порядок: подробные блоки, затем итог. */}
              {showGazVehicleSummary ? (
                <>
                  <GazProposalSummary
                    totals={gazProposalTotals}
                    metrics={gazSummaryMetrics}
                    vehicleModel={gazVehicleLoadModel}
                    headline={gazProposalHeadline}
                    totalLabel={gazTotalLabel}
                    totalValue={gazManualTotalValue || (gazCashTotal.applies
                      ? formatGazPayable(gazCashTotal.total, true)
                      : (gazProposalTotal != null
                        ? formatGazPayable(gazProposalTotal, false)
                        : `${Math.ceil(totalSum).toLocaleString('ru-RU')} ₽`))}
                    fullTruckBasis={gazFullTruckBasis}
                    displayVolumeM3={gazActiveSendOption?.volume || gazOrderFacts?.factVolume}
                    facts={gazOrderFacts}
                    cheapestHint={gazCheapestHint}
                    brandHint={gazBrandHint}
                    onSelectProduction={onSelectGazProduction}
                    hasUpsells={gazProposalUpsells.length > 0}
                    onShowComparison={handleShowComparison}
                    deliveryDistanceKm={offer?.delivery?.distanceProdToClient}
                    photo={{ available: gazPhoto.available, urls: gazPhoto.urls, onOpen: handleOpenGazPhoto }}
                  />

                  {/* Распределение по машинам — основное содержание КП внутри области:
                      подробные блоки «Основной блок» и «Остаток и отдельные услуги»
                      здесь больше не рисуются. Их сборщик остаётся нетронутым: по нему
                      считаются суммы, состав КП и текст сообщения, а вне области он
                      по-прежнему и рисуется. */}
                  <div style={{ minWidth: 0 }}>
                    {/* Готовое КП видно сразу, без нажатия и без модального окна:
                        менеджер правит текст на месте и копирует. Машины рисуются
                        внутри карточки своего варианта. */}
                    <GazClientProposal
                      options={gazSendOptions}
                      mode={gazSendMode}
                      onModeChange={handleGazSendModeChange}
                      renderVehicles={renderVariantVehicles}
                      priceModel={gazProposalPriceModel}
                      priceOverrides={gazPriceOverrides}
                      onPriceOverridesChange={setGazPriceOverrides}
                    />
                  </div>
                </>
              ) : (
                <>
                  {gazCommercialProposal.blocks.map(renderGazBlockSection)}

                  <div className="rd-kp-totals">
                    <div className="rd-kp-totals__label">
                      {isOriginal ? 'К оплате' : 'Итого'}
                    </div>
                    <div className="rd-kp-totals__value">
                      {gazProposalTotal != null
                        ? `${(Math.round(gazProposalTotal * 100) / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`
                        : `${Math.ceil(totalSum).toLocaleString('ru-RU')} ₽`}
                    </div>
                    <div className="rd-kp-totals__meta">
                      {isOriginal
                        ? <>{per.text} <b>{Math.abs(resultSkidkaOrMarzha(gazProposalTotal ?? totalSum, baseSum, per.id)).toLocaleString('ru-RU')} ₽</b> · <b>{procentSkidkaOrMarzha(gazProposalTotal ?? totalSum, baseSum, per.id)}%</b></>
                        : <>Выгода <b>{Math.abs(resultSkidkaOrMarzha(gazProposalTotal ?? totalSum, baseSum, per.id)).toLocaleString('ru-RU')} ₽</b></>
                      }
                    </div>
                  </div>
                </>
              )}

              {/* Две кнопки выбора сценария заменены единым окном формирования КП,
                  поэтому внутри области не рисуются. Вне области — как в 5442898. */}
              {isOriginal && !isMskGazobetonScope && onCommercialProposalSendModeChange && (
                <div className="rd-kp-scenario">
                  <button
                    className={`rd-kp-scenario__btn${commercialProposalSendMode === 'partial' ? ' is-active' : ''}`}
                    onClick={() => onCommercialProposalSendModeChange('partial')}
                  >
                    КП - полная загрузка шаланд
                  </button>
                  <button
                    className={`rd-kp-scenario__btn${commercialProposalSendMode === 'full' ? ' is-active' : ''}`}
                    onClick={() => onCommercialProposalSendModeChange('full')}
                  >
                    КП - весь объем
                  </button>
                </div>
              )}

              <div className="rd-kp-actions">
                <button
                  className="rd-kp-actions__btn rd-kp-actions__btn--b24"
                  onClick={() => onFillBitrix(offer)}
                  title="Заполнить в сделке Bitrix"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19.35 10.04C18.67 6.59 15.64 4.25 12 4.25c-2.89 0-5.4 1.64-6.65 4.04C2.34 8.61 0 11.16 0 14.25c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-5.21z" fill="white" />
                    <text x="12" y="17.4" textAnchor="middle" fontSize="8.2" fontWeight="900" fill="#1f88c8" fontFamily="-apple-system,Inter,Arial,sans-serif" letterSpacing="-0.3">24</text>
                  </svg>
                </button>
                {/* Кнопка открывала модальное окно сообщения. Внутри области его
                    больше нет — КП уже готово на странице. Вне области кнопка и
                    окно работают как в 5442898. */}
                {!isMskGazobetonScope && (
                <button
                  className="rd-kp-actions__btn rd-kp-actions__btn--msg"
                  onClick={() => onCreateMessage(offer)}
                  title="Сформировать сообщение"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="white">
                    <path d="M256 32C114.6 32 0 125.1 0 240c0 49.6 21.4 95 57 130.7C44.5 421.1 2.7 466 2.2 466.5c-2.2 2.3-2.8 5.7-1.5 8.7S4.8 480 8 480c66.3 0 116-31.8 140.6-51.4 32.7 12.3 69 19.4 107.4 19.4 141.4 0 256-93.1 256-208S397.4 32 256 32z" />
                  </svg>
                </button>
                )}
                {/* <button
                  className="rd-kp-actions__btn rd-kp-actions__btn--max"
                  onClick={() => onSendToMax(offer)}
                  title="Отправить в MAX"
                >
                  <img src="https://maxicons.ru/icons/Max_logo_black.svg" alt="MAX" style={{ width: '60%', height: '60%', filter: 'brightness(0) invert(1)' }} />
                </button> */}
              </div>
            </div>
          ) : (
            <div>
              <table className="rd-kp-table">
                <thead>
                  <tr>
                    <th>№</th>
                    <th className="rd-kp-th--text">Наименование</th>
                    <th>Кол-во</th>
                    <th>Ед.</th>
                    <th>Цена / ед.</th>
                    <th>Сумма</th>
                    {isOriginal && (
                      <th
                        style={{ textDecoration: 'underline', color: 'var(--rd-blue-600)', cursor: 'pointer' }}
                        onClick={onSkidkaOrMarzha}
                      >
                        {per.text}
                      </th>
                    )}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {offer.materials.map((material, index) => (
                    <React.Fragment key={material.name + index}>
                      <tr>
                        <td className="text-center">{index + 1}</td>
                        <td
                          className="rd-kp-td--text"
                          onMouseEnter={(e) => handleMouseEnter(e, material)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', position: 'relative', width: '100%' }}>
                            <span style={{
                              textAlign: 'left',
                              paddingRight: getItemsForRow(index).length > 0 ? '60px' : '0',
                              width: '100%'
                            }}>{material.name}</span>
                            {getItemsForRow(index).length > 0 && (
                              <button
                                onClick={() => toggleRowExpansion(index)}
                                style={{
                                  position: 'absolute',
                                  right: '0',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: '1px solid #1976d2',
                                  backgroundColor: 'transparent',
                                  color: '#1976d2',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  flexShrink: 0,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                {expandedRows[index] ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                                <span>{getItemsForRow(index).length}</span>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="text-center">{formatQuantity(material.quantity)}</td>
                        <td className="text-center">{material.unit}</td>
                        <td className="text-center">
                          <NumberInput
                            value={isOriginal ? safeCeil(localPrices[index], material.pricePerOne) : safeCeil(material.pricePerOne)}
                            onChange={(value) => onMaterialPriceChange(index, value, offerType)}
                            style={{ width: '100px' }}
                          />
                        </td>
                        <td className="text-center">{Math.ceil(material.sum).toLocaleString('ru-RU')} руб.</td>
                        {isOriginal && (
                          <td className="text-center">
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <NumberInput
                                value={localSkidka[index] || ''}
                                onChange={(value) => onSkidkaChange(index, value, offerType)}
                                style={{ width: '80px' }}
                              />
                              <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>%</span>
                            </div>
                          </td>
                        )}
                        <td className="text-center">
                          {material.isFromInsulationFriends && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onRemoveMaterial(index)}
                              title="Удалить материал"
                            >
                              <span style={{ fontSize: '16px' }}>✕</span>
                            </IconButton>
                          )}
                        </td>
                      </tr>
                      {expandedRows[index] && getItemsForRow(index).length > 0 && (
                        <InsulationFriendsList
                          items={getItemsForRow(index)}
                          onSelectMaterial={handleSelectMaterial}
                          onRemoveMaterial={handleRemoveMaterial}
                          currentIndex={index}
                          isOriginal={isOriginal}
                          colSpan={isOriginal ? 8 : 7}
                          setExpandedRows={setExpandedRows}
                          paymentMethod={paymentMethod}
                          offerMaterials={offer.materials}
                          freePalletsForUpsells={freePalletsForUpsells}
                          isMskGazobetonScope={isMskGazobetonScope}
                        />
                      )}
                    </React.Fragment>
                  ))}
                  {offer.delivery && offer.delivery.name !== 'Самовывоз' && (
                    <tr key="delivery">
                      <td className="text-center">{offer.materials.length + 1}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: '100%' }}>
                          <span style={{
                            textAlign: 'center',
                            paddingRight: (isOriginal && selectedProduction !== "ГРАНД ЛАЙН - ЦЕНТР ООО") ? '120px' : '0',
                            width: '100%'
                          }}>{offer.delivery.name}</span>
                          {isOriginal && selectedProduction !== "ГРАНД ЛАЙН - ЦЕНТР ООО" && (
                            <div
                              style={{
                                position: 'absolute',
                                right: '0',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              {underfilledShalandaWarning && (
                                <button
                                  onClick={toggleUnderfilledShalandaModal}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    border: '1px solid #d97706',
                                    backgroundColor: showUnderfilledShalandaModal ? 'rgba(217, 119, 6, 0.12)' : 'transparent',
                                    color: '#d97706',
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                  }}
                                  title="Есть недозаполненная шаланда"
                                >
                                  !
                                </button>
                              )}
                              {canChangeDeliveryMethod && (
                                <button
                                  onClick={toggleDeliveryAlternatives}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #1976d2',
                                    backgroundColor: 'transparent',
                                    color: '#1976d2',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    marginLeft: '8px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {expandedDeliveryRow ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                                  <span>Другие</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        {offer.delivery.numberOfTrips.toLocaleString()}
                      </td>
                      <td
                        className="text-center"
                        style={{ whiteSpace: 'nowrap' }}
                        onMouseEnter={isGazDeliveryOffer ? handleTripsMouseEnter : undefined}
                        onMouseLeave={isGazDeliveryOffer ? handleTripsMouseLeave : undefined}
                      >
                        рейс
                      </td>
                      <td className="text-center">
                        <NumberInput
                          value={isOriginal ? localDeliveryPrice : Math.ceil(offer.delivery.costPerTrip)}
                          onChange={(value) => onDeliveryPriceChange(value, offerType)}
                          style={{ width: '100px' }}
                        />
                      </td>
                      <td className="text-center">{Math.ceil(offer.delivery.totalCost).toLocaleString('ru-RU')} руб.</td>
                      {isOriginal && (
                        <td className="text-center">
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <NumberInput
                              value={localSkidkaD || ''}
                              onChange={(value) => onSkidkaChangeDelivery(value, offerType)}
                              style={{ width: '80px' }}
                            />
                            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>%</span>
                          </div>
                        </td>
                      )}
                      <td></td>
                    </tr>
                  )}
                  {offer.delivery && offer.delivery.name !== 'Самовывоз' && expandedDeliveryRow && canChangeDeliveryMethod && isOriginal && selectedProduction !== "ГРАНД ЛАЙН - ЦЕНТР ООО" && (
                    <DeliveryAlternativesList
                      alternatives={deliveryAlternatives}
                      onSelectDelivery={handleSelectDeliveryAlternative}
                      colSpan={isOriginal ? 8 : 7}
                      setExpandedRows={setExpandedDeliveryRow}
                      currentDelivery={offer.delivery}
                      paymentMethod={paymentMethod}
                      isLoading={isLoadingAlternatives}
                    />
                  )}
                </tbody>
              </table>

              <div className="rd-kp-totals">
                <div className="rd-kp-totals__label">
                  {isOriginal ? 'К оплате' : 'Итого'}
                </div>
                <div className="rd-kp-totals__value">
                  {Math.ceil(totalSum).toLocaleString('ru-RU')} ₽
                </div>
                <div className="rd-kp-totals__meta">
                  {isOriginal
                    ? <>{per.text} <b>{Math.abs(resultSkidkaOrMarzha(totalSum, baseSum, per.id)).toLocaleString('ru-RU')} ₽</b> · <b>{procentSkidkaOrMarzha(totalSum, baseSum, per.id)}%</b></>
                    : <>Выгода <b>{Math.abs(resultSkidkaOrMarzha(totalSum, baseSum, per.id)).toLocaleString('ru-RU')} ₽</b></>
                  }
                </div>
              </div>

              <div className="rd-kp-actions">
                <button
                  className="rd-kp-actions__btn rd-kp-actions__btn--b24"
                  onClick={() => onFillBitrix(offer)}
                  title="Заполнить в сделке Bitrix"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19.35 10.04C18.67 6.59 15.64 4.25 12 4.25c-2.89 0-5.4 1.64-6.65 4.04C2.34 8.61 0 11.16 0 14.25c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-5.21z" fill="white" />
                    <text x="12" y="17.4" textAnchor="middle" fontSize="8.2" fontWeight="900" fill="#1f88c8" fontFamily="-apple-system,Inter,Arial,sans-serif" letterSpacing="-0.3">24</text>
                  </svg>
                </button>
                <button
                  className="rd-kp-actions__btn rd-kp-actions__btn--msg"
                  onClick={() => onCreateMessage(offer)}
                  title="Сформировать сообщение"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="white">
                    <path d="M256 32C114.6 32 0 125.1 0 240c0 49.6 21.4 95 57 130.7C44.5 421.1 2.7 466 2.2 466.5c-2.2 2.3-2.8 5.7-1.5 8.7S4.8 480 8 480c66.3 0 116-31.8 140.6-51.4 32.7 12.3 69 19.4 107.4 19.4 141.4 0 256-93.1 256-208S397.4 32 256 32z" />
                  </svg>
                </button>
                {/* <button
                  className="rd-kp-actions__btn rd-kp-actions__btn--max"
                  onClick={() => onSendToMax(offer)}
                  title="Отправить в MAX"
                >
                  <img src="https://maxicons.ru/icons/Max_logo_black.svg" alt="MAX" style={{ width: '60%', height: '60%', filter: 'brightness(0) invert(1)' }} />
                </button> */}
              </div>
            </div>
          )}
        </div>
        {/* Окно дополнения машины — только внутри МСК Газобетона и только когда
            варианты дополнения действительно есть. */}
        {upsellVehicle && hasUpsellOptions && (
          <GazUpsellModal
            vehicle={upsellVehicle}
            items={gazUpsellItems}
            paymentMethod={paymentMethod}
            onClose={() => setUpsellVehicle(null)}
            onSubmit={handleUpsellSubmit}
          />
        )}
        {showUnderfilledShalandaModal && createPortal(
          <div className="rd-modal-overlay" role="presentation">
            <div
              className="rd-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rd-shalanda-title"
            >
              <button
                type="button"
                className="rd-modal__close"
                onClick={closeUnderfilledShalandaModal}
                disabled={isApplyingUnderfilledReduction}
                aria-label="Закрыть"
                title="Закрыть"
              />
              <div className="rd-modal__head">
                <h3 id="rd-shalanda-title" className="rd-modal__title">Недозаполненная шаланда</h3>
              </div>
              <div className="rd-modal__body">
                {underfilledShalandaWarning && (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div className="rd-modal__stat">Количество шаланд: <strong>{underfilledShalandaWarning.shalandaCount}</strong></div>
                    <div className="rd-modal__stat">Количество полностью заполненных шаланд: <strong>{underfilledShalandaWarning.fullShalandaCount}</strong></div>
                    <div className="rd-modal__stat">Поддонов в недозаполненной шаланде: <strong>{underfilledShalandaWarning.underfilledPallets}</strong></div>
                    {underfilledReductionCandidates.length > 0 && (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        <div className="rd-modal__stat"><strong>Выберите товар, из которого убрать поддоны:</strong></div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {underfilledReductionCandidates.map((candidate) => (
                            <label
                              key={candidate.index}
                              className={`rd-modal__choice${selectedReductionMaterialIndex === candidate.index ? ' rd-modal__choice--active' : ''}${!candidate.isAvailable ? ' rd-modal__choice--disabled' : ''}`}
                            >
                              <div className="rd-modal__choice-head">
                                <input
                                  type="radio"
                                  name={`underfilled-shalanda-${offerType}`}
                                  checked={selectedReductionMaterialIndex === candidate.index}
                                  disabled={!candidate.isAvailable || isApplyingUnderfilledReduction}
                                  onChange={() => setSelectedReductionMaterialIndex(candidate.index)}
                                />
                                <span>{candidate.name}</span>
                              </div>
                              <div className="rd-modal__choice-meta">
                                Кол-во: <strong>{formatQuantity(candidate.quantity)}</strong> | Нужно убрать: <strong>{formatQuantity(candidate.quantityToRemove)}</strong> м3
                              </div>
                              {!candidate.isAvailable && (
                                <div className="rd-modal__choice-warn">{candidate.disabledReason}</div>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {isLoadingUnderfilledPreview ? (
                      <div className="rd-modal__note rd-modal__note--loading">
                        <CircularProgress size={18} />
                        <span>Считаю новый итог заказа...</span>
                      </div>
                    ) : underfilledShalandaPreview ? (
                      <div className="rd-modal__note">
                        Если убрать из заказа <strong>{underfilledShalandaWarning.underfilledPallets}</strong> поддонов, то общая стоимость заказа будет: <strong>{Math.ceil(underfilledShalandaPreview.totalOrderCost).toLocaleString('ru-RU')} руб.</strong>
                      </div>
                    ) : null}
                    {underfilledPreviewError && (
                      <div className="rd-modal__note rd-modal__note--error">{underfilledPreviewError}</div>
                    )}
                  </div>
                )}
              </div>
              <div className="rd-modal__foot">
                <div className="rd-modal__actions">
                  <button
                    type="button"
                    className="rd-modal__btn rd-modal__btn--ghost"
                    onClick={closeUnderfilledShalandaModal}
                    disabled={isApplyingUnderfilledReduction}
                  >
                    Закрыть
                  </button>
                  <button
                    type="button"
                    className="rd-modal__btn rd-modal__btn--primary"
                    onClick={confirmUnderfilledShalandaReduction}
                    disabled={isLoadingUnderfilledPreview || isApplyingUnderfilledReduction || !underfilledShalandaPreview}
                  >
                    {isApplyingUnderfilledReduction ? 'Пересчитываю...' : 'Подтвердить'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Свой просмотрщик, а не общий: кнопка «Скопировать фото» нужна только
          здесь, а общий используют таблицы производств на всех направлениях. */}
      <GazPhotoViewer
        isOpen={gazPhoto.isOpen}
        title={gazProposalHeadline?.production || offer?.production || ''}
        imageUrls={gazPhoto.urls}
        isLoading={gazPhoto.isLoading}
        onClose={handleCloseGazPhoto}
      />
    </>
  );
};

export default OfferTable;
